import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AuthService } from '@app/services/auth.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import * as Y from 'yjs';
import { WebrtcProvider } from 'y-webrtc';
import { Awareness } from 'y-protocols/awareness';

export interface CollabUser { username: string; email: string; color: string; avatar: string; }

export interface ChatMessage {
  id: string;
  username: string;
  color: string;
  avatar: string;
  text: string;
  timestamp: number;
}

export interface PeerState {
  username: string; color: string; avatar: string;
  cursorX: number; cursorY: number; activeTool: string;
  activeFrameId: string | null;
  clientId: number; role: RoomRole; isOnline: boolean;
  pixelFormatVersion?: number;
}

export interface JoinRequest {
  id: string; username: string; email: string; color: string; avatar: string;
  timestamp: number; password?: string;
}

export type RoomRole = 'admin' | 'member' | 'spectator' | 'pending' | 'none';

export interface CollabSettings {
  adminOnlyFrameCreate: boolean;
  adminOnlyFrameDelete: boolean;
  adminOnlyCanvasResize: boolean;
  membersDrawOnly: boolean;
  broadcastAiGenerations: boolean;
  showCursorLabels: boolean;
  roomPassword: string;
  canvasWidth: number;
  canvasHeight: number;
  chatEnabled: boolean;
  maxUsers: number;
  autoRejoin: boolean;
  allowMembersSaveCanvas: boolean;
}

export const DEFAULT_COLLAB_SETTINGS: CollabSettings = {
  adminOnlyFrameCreate: true,
  adminOnlyFrameDelete: true,
  adminOnlyCanvasResize: true,
  membersDrawOnly: false,
  broadcastAiGenerations: true,
  showCursorLabels: true,
  roomPassword: '',
  canvasWidth: 54,
  canvasHeight: 96,
  chatEnabled: true,
  maxUsers: 12,
  autoRejoin: true,
  allowMembersSaveCanvas: false,
};

const SIGNALING_SERVERS = ['wss://yjs-signalling.fly.dev'];
const WARN_THRESHOLD = 6;
const MAX_USERS_HARD_CAP = 50;
const PIXEL_FORMAT_VERSION = 2;
/** Tag every local Yjs transaction so observeDeep can skip self-generated events */
const LOCAL_ORIGIN = 'pxlvd-local';

export const USER_COLORS = ['#ff6b9d','#ffa94d','#69db7c','#74c0fc','#da77f2','#ff8787','#63e6be','#ffd43b','#a9e34b','#4dabf7','#e599f7','#ff6b6b'];

@Injectable({ providedIn: 'root' })
export class CollaborationService implements OnDestroy {

  isInRoom$           = new BehaviorSubject<boolean>(false);
  role$               = new BehaviorSubject<RoomRole>('none');
  roomId$             = new BehaviorSubject<string | null>(null);
  peers$              = new BehaviorSubject<PeerState[]>([]);
  joinRequests$       = new BehaviorSubject<JoinRequest[]>([]);
  performanceWarning$ = new BehaviorSubject<string | null>(null);
  connectionStatus$   = new BehaviorSubject<'disconnected'|'connecting'|'connected'>('disconnected');
  collabSettings$     = new BehaviorSubject<CollabSettings>({ ...DEFAULT_COLLAB_SETTINGS });

  /** Emits whenever a new chat message arrives (from any peer including self via Yjs) */
  chatMessage$ = new Subject<ChatMessage>();
  /** Usernames whose chat posting is currently muted by admin. */
  chatMutedUsers$ = new BehaviorSubject<string[]>([]);

  /** Emits when the local user is kicked or leaves - callers should wipe the collab canvas */
  canvasWipe$ = new Subject<void>();

  /** Username of the user who originally created this room (cannot be demoted) */
  roomCreator$ = new BehaviorSubject<string | null>(null);

  /** Usernames currently on the banned list for this room */
  bannedUsers$ = new BehaviorSubject<string[]>([]);

  /**
   * Fires when the user leaves a room or creates a new one so consumers
   * (e.g. right-side-bar chat) can clear their locally cached message lists.
   */
  chatCleared$ = new Subject<void>();

  /**
   * Emits when the joiner detects a resolution mismatch between their local canvas
   * and the room's canvas.
   */
  resolutionMismatch$ = new Subject<{ roomWidth: number; roomHeight: number; localWidth: number; localHeight: number }>();

  /** Emits true when a bulk frame sync is in progress on non-admin peers */
  syncing$ = new BehaviorSubject<boolean>(false);
  /** True only after an approved non-admin has applied the admin's full sync snapshot */
  collabHydrated$ = new BehaviorSubject<boolean>(false);

  /** Track whether we actually joined a room (vs just connecting as admin) */
  private _wasInRoom = false;

  /** Fired once when a member joins - full snapshot of all frames */
  remoteFullSync$ = new Subject<{ frames: { frameId: string; pixels: string[][] }[]; frameOrder: string[] }>();
  /** Fired whenever any remote peer changes any frame's pixels */
  remotePixelChange$ = new Subject<{ frameId: string; pixels: string[][] }>();
  remoteFrameDelete$ = new Subject<string>();
  remoteFrameOrder$  = new Subject<string[]>();
  /** Fired whenever a remote peer changes the layer structure */
  remoteLayerSync$ = new Subject<{ layers: any[]; activeLayerId: string | null }>();
  /** Fired whenever a remote peer updates one or more cels (sparse pixel layer data) */
  remoteCelChange$ = new Subject<{ cels: Record<string, string[][]> }>();
  /** Fired whenever a remote peer's undo/redo cleared a cel (layer reverted to blank) */
  remoteCelClear$ = new Subject<{ celKeys: string[] }>();

  localUser: CollabUser | null = null;
  readonly signalingMode: 'local'|'public' = 'public';
  readonly localSignalingUrl = null;

  private ydoc: Y.Doc | null = null;
  private provider: WebrtcProvider | null = null;
  private awareness: Awareness | null = null;
  private yFrames: Y.Map<any> | null = null;
  private yFrameOrder: Y.Array<string> | null = null;
  private yLayers: Y.Map<any> | null = null;
  private yJoinRequests: Y.Map<any> | null = null;
  private yAllowedUsers: Y.Map<string> | null = null;
  private ySettings: Y.Map<any> | null = null;
  private yChat: Y.Array<any> | null = null;
  private yChatMutedUsers: Y.Map<any> | null = null;
  private yBannedUsers: Y.Map<any> | null = null;
  /** Sparse cel data: key = "frameId|layerId", value = Y.Map with 'pd'/'w'/'h' */
  private yCels: Y.Map<any> | null = null;
  private _lastChatLength = 0;
  /** True once the Yjs doc has synced with at least one peer.
   *  Gates yChat.observe so historical messages don't re-emit as new chat. */
  private _chatSynced = false;

  private _destroy$ = new Subject<void>();
  private _signalingIndex = 0;
  private _reconnectTimer: any = null;
  private _approvedViaAwareness = false;
  /** True when reconnecting as a previously-approved member/spectator after a page refresh. */
  private _isRejoin = false;
  /** True while refresh rejoin policy must be enforced for the active join attempt. */
  private _rejoinPolicyActive = false;
  /**
   * Room autoRejoin value resolved from the synced room settings during a refresh rejoin.
   * null means "not resolved yet" and must be treated conservatively (approval required).
   */
  private _rejoinAutoRejoinEnabled: boolean | null = null;
  /** Timestamp for the current pending join attempt (guards against stale approval signals). */
  private _pendingJoinStartedAt = 0;
  private _lastApprovalTs = 0; // tracks last processed approval to prevent re-triggering
  /** Grace window to avoid false kicks while allowedUsers is briefly desynced. */
  private _missingAllowedRoleSinceMs = 0;
  /** Cancels the pending-join broadcast loop when called */
  private _stopPendingBroadcast: (() => void) | null = null;
  /** Cancels the active-approval poll loop when called */
  private _stopPollApproval: (() => void) | null = null;
  /** Tracks the timestamp of the last kick signal processed - prevents stale re-kicks */
  private _lastKickTs = 0;
  /** Guards against duplicate remoteFullSync$ emissions from concurrent synced/peers events */
  private _fullSyncEmitted = false;
  /**
   * Set to true when the y-webrtc 'synced' event fires, indicating that the Yjs
   * document now reflects the current room state from the network (not just the
   * local IndexedDB cache).  Used by yAllowedUsers.observe to know whether it is
   * safe to call _emitFullSync() (data is current) or whether it must wait for the
   * 'synced' event to deliver up-to-date data first.
   * Cleared in _teardown() so each new connection starts fresh.
   */
  private _providerSynced = false;
  /** Tracks WebRTC peer IDs the admin has already broadcast frames to */
  private _knownPeers = new Set<string>();
  /** Prevent repeated mismatch handling loops in awareness updates */
  private _pixelFormatMismatchHandled = false;
  /** Maps awareness clientId → username for non-creator admins currently online.
   *  Used by the creator admin to detect when a non-creator admin goes offline
   *  and automatically demote them back to member. */
  private _onlineNonCreatorAdmins = new Map<number, string>();
  /** True when this user is rejoining as a non-creator (caps max role to member on approval). */
  private _isCreatorRejoin = false;
  /**
   * True when this session started via requestJoin() (i.e. the user was initially
   * 'pending' before getting a role).  Even if the resolved role turns out to be
   * 'admin' (e.g. the room creator rejoining), they must still receive a full sync
   * from the current Yjs state — they are no longer the authoritative source of truth.
   * Cleared in _teardown() so a subsequent createRoom() starts fresh.
   */
  private _joinedViaRequestJoin = false;
  /** Cancel handle for an in-progress chunked broadcast */
  private _chunkedBroadcastTimer: any = null;
  /** Fallback timer: re-triggers full-sync emit if hydration acknowledgement is delayed. */
  private _hydrateFallbackTimer: any = null;
  /** Last processed room-close timestamp (deduplicates awareness replays). */
  private _lastRoomClosedTs = 0;
  /** Tracks remote active usernames for join/leave system chat messages. */
  private _lastActiveRemoteUsers = new Set<string>();
  private _presenceInitialized = false;
  /**
   * Per-frame throttle state for broadcastFrame.
   * Stores the trailing-edge timer handle and the latest pending pixel payload
   * so rapid draws are coalesced into at most one Yjs write per 30 ms window.
   */
  private _broadcastThrottle = new Map<string, {
    timer: any;
    pixels: string[][];
    width: number;
    height: number;
  }>();
  /** Timestamp of the last actual Yjs write per frameId (for leading-edge test) */
  private _lastBroadcastMs = new Map<string, number>();
  /** Throttle window in ms - 30 ms ≈ 33 fps, imperceptible vs. P2P latency */
  private static readonly BROADCAST_THROTTLE_MS = 30;

  /** Applied once at class-load time (static flag) so the RTCDataChannel prototype
   *  is only patched once even if multiple CollaborationService instances exist. */
  private static _rtcChunkPatchApplied = false;

  constructor(private zone: NgZone, private auth: AuthService) {
    this._patchRTCDataChannelForChunking();
    this._loadLocalUser();
    // Sync collab user from Supabase profile when auth state changes
    this.auth.state$.subscribe(async state => {
      if (state.user) {
        const profile = await this.auth.getProfile();
        if (profile?.username) {
          // Always sync from DB so username changes reflect immediately
          this.localUser = {
            username: profile.username,
            email: state.user.email ?? '',
            color: this.localUser?.color ?? this.getRandomColor(),
            avatar: profile.avatar ?? '🎨',
          };
          localStorage.setItem('pxlvd_collab_user', JSON.stringify(this.localUser));
        }
      }
    });
  }

  private _loadLocalUser(): void {
    try { const s = localStorage.getItem('pxlvd_collab_user'); if (s) this.localUser = JSON.parse(s); } catch {}
  }

  saveLocalUser(u: CollabUser): void {
    this.localUser = u;
    localStorage.setItem('pxlvd_collab_user', JSON.stringify(u));
  }

  getRandomColor(): string { return USER_COLORS[Math.floor(Math.random() * USER_COLORS.length)]; }
  generateRoomId(): string { return Math.random().toString(36).slice(2,9).toUpperCase(); }

  getLastRoom(): { roomId: string; role: RoomRole; isCreator?: boolean } | null {
    try { const s = localStorage.getItem('pxlvd_collab_last'); return s ? JSON.parse(s) : null; } catch { return null; }
  }

  private persistLastRoom(role: RoomRole, roomId?: string | null): void {
    const targetRoomId = roomId ?? this.roomId$.value;
    if (!targetRoomId) return;
    const existing = this.getLastRoom();
    const creatorFromRoom = (this.ySettings?.get('roomCreator') as string | undefined) === this.localUser?.username;
    const isCreator = creatorFromRoom || existing?.isCreator === true;
    localStorage.setItem('pxlvd_collab_last', JSON.stringify({ roomId: targetRoomId, role, isCreator }));
  }

  can(action: 'createFrame'|'deleteFrame'|'resizeCanvas'|'draw'|'aiGenerate'|'kick'|'promote'|'settings'|'saveCanvas'|'createLayer'|'deleteLayer'): boolean {
    const role = this.role$.value;
    const s = this.collabSettings$.value;
    if (role === 'none') return true;
    if (role === 'pending' && this.isInRoom$.value && this._approvedViaAwareness) return action === 'draw';
    if (role === 'spectator' || role === 'pending') return false;
    if (role === 'admin') return true;
    switch (action) {
      case 'createFrame':  return !s.adminOnlyFrameCreate;
      case 'deleteFrame':  return !s.adminOnlyFrameDelete;
      case 'resizeCanvas': return !s.adminOnlyCanvasResize;
      case 'draw':         return true;
      case 'aiGenerate':   return !s.membersDrawOnly;
      case 'saveCanvas':   return s.allowMembersSaveCanvas;
      // Layer management is a structural operation; blocked in draw-only mode.
      case 'createLayer':  return !s.membersDrawOnly;
      case 'deleteLayer':  return !s.membersDrawOnly;
      default:             return !s.membersDrawOnly;
    }
  }

  /** Outbound frame/order/delete sync is blocked until a member is hydrated from admin snapshot. */
  canBroadcastCanvasSync(): boolean {
    const role = this.role$.value;
    if (!this.isInRoom$.value) return false;
    // A rejoining admin must finish receiving the current room state before they
    // can broadcast — otherwise they'd push stale IndexedDB data over the room.
    if (role === 'admin') return this._joinedViaRequestJoin ? this.collabHydrated$.value : true;
    if (role === 'pending' || role === 'spectator' || role === 'none') return false;
    return this.collabHydrated$.value;
  }

  /** Called by the editor after remoteFullSync is applied to the store. */
  markCanvasHydrated(): void {
    // Original admins (createRoom path) are authoritative from the start and are
    // already hydrated.  However a rejoining admin (_joinedViaRequestJoin) starts
    // in syncing state just like a member and needs this call to clear the overlay
    // and unblock incremental updates.
    if (this.role$.value === 'admin' && !this._joinedViaRequestJoin) return;
    clearTimeout(this._hydrateFallbackTimer);
    this.collabHydrated$.next(true);
    this.syncing$.next(false);
  }

  createRoom(roomId: string, frames: {id:string;pixels:string[][];width:number;height:number}[], settings: Partial<CollabSettings> = {}, password = '', cels: Record<string, string[][]> = {}, layers: any[] = [], activeLayerId: string | null = null): void {
    if (!this.localUser) throw new Error('User profile required');
    // Clear any chat messages from a previous room session before starting fresh.
    this.chatCleared$.next();
    this._teardown();
    const merged: CollabSettings = {
      ...DEFAULT_COLLAB_SETTINGS, ...settings, roomPassword: password,
      canvasWidth: frames[0]?.width ?? DEFAULT_COLLAB_SETTINGS.canvasWidth,
      canvasHeight: frames[0]?.height ?? DEFAULT_COLLAB_SETTINGS.canvasHeight
    };
    this._connect(roomId, 'admin', frames, merged, '', cels, layers, activeLayerId);
    // Fix: _connect writes roomCreator to ySettings inside a ydoc transaction, but
    // the ySettings.observe callback (which sets roomCreator$) is registered AFTER
    // that transaction completes.  Yjs observers only fire for changes that happen
    // AFTER they are registered, so roomCreator$ would stay null for the creator and
    // isRoomCreator in the collab panel would return false.  Set it directly here.
    this.roomCreator$.next(this.localUser!.username);
    // isCreator:true marks this user as the permanent room creator - used to prevent
    // accidental demotion and to distinguish creator from promoted admins on refresh.
    localStorage.setItem('pxlvd_collab_last', JSON.stringify({ roomId, role: 'admin', isCreator: true }));
  }

  requestJoin(roomId: string, password = '', isRejoin = false, isCreator = false): void {
    if (!this.localUser) throw new Error('User profile required');
    this._teardown();
    this.collabHydrated$.next(false);
    this.syncing$.next(false);
    this._pendingJoinStartedAt = Date.now();
    this._missingAllowedRoleSinceMs = 0;
    this._isRejoin = isRejoin;
    this._rejoinPolicyActive = isRejoin;
    this._rejoinAutoRejoinEnabled = isRejoin ? null : this.collabSettings$.value.autoRejoin;
    // Non-creator users (promoted admins, members) must never auto-upgrade to admin
    // via a stale yAllowedUsers entry.  _recheckApproval reads this flag to cap the role.
    this._isCreatorRejoin = isCreator;
    // Mark that this session started as 'pending' so that even if the resolved
    // role is 'admin' (room creator rejoining), _emitFullSync is allowed to run.
    // The rejoining admin is NOT the authoritative source — the current room state
    // lives in the Yjs document and must be pulled into the Angular store.
    this._joinedViaRequestJoin = true;
    this._connect(roomId, 'pending', [], null, password);
    // NOTE: We do NOT set isInRoom$ or _wasInRoom here for rejoin.
    // yAllowedUsers.observe will set them once Yjs syncs and confirms the role.
    // _isRejoin=true tells that observer to skip the premature eviction check.
  }

  leaveRoom(): void {
    // Notify consumers to clear their chat caches before teardown.
    this.chatCleared$.next();
    const wasJoined = this._wasInRoom;
    if (this.ydoc && this.localUser && this.yAllowedUsers && this.yJoinRequests) {
      const username = this.localUser.username;
      const creator = this.ySettings?.get('roomCreator') as string | undefined;
      const isCreatorAdmin = this.role$.value === 'admin' && creator === username;
      // Voluntary leave should invalidate prior approval state for non-creator users.
      // Otherwise stale allowedUsers records can cause silent re-entry without a fresh request.
      if (!isCreatorAdmin) {
        this.ydoc.transact(() => {
          try { this.yAllowedUsers!.delete(username); } catch {}
          try { this.yJoinRequests!.delete(`req-${username}`); } catch {}
          try { this.yJoinRequests!.delete(`approved-${username}`); } catch {}
        }, LOCAL_ORIGIN);
      }
    }
    localStorage.removeItem('pxlvd_collab_last');
    this._teardown();
    this.isInRoom$.next(false); this.role$.next('none'); this.roomId$.next(null);
    this.peers$.next([]); this.joinRequests$.next([]); this.performanceWarning$.next(null);
    this.connectionStatus$.next('disconnected'); this.collabSettings$.next({ ...DEFAULT_COLLAB_SETTINGS });
    this.collabHydrated$.next(false);
    this.syncing$.next(false);
    this._wasInRoom = false;
    // Emit wipe signal so listeners clear the collab canvas from memory
    if (wasJoined) this.canvasWipe$.next();
  }

  approveRequest(requestId: string, asRole: 'member'|'spectator' = 'member'): void {
    if (!this.ydoc || !this.yAllowedUsers || !this.yJoinRequests) return;
    let username: string | null = null;
    let yKey: string | null = null;
    if (requestId.startsWith('awareness-')) {
      username = requestId.slice('awareness-'.length);
      this.yJoinRequests!.forEach((v: JoinRequest, k: string) => { if (v.username === username) yKey = k; });
    } else {
      const req = this.yJoinRequests.get(requestId);
      if (!req) return;
      username = req.username; yKey = requestId;
    }
    if (!username) return;
    // Primary path: write to yAllowedUsers (LOCAL_ORIGIN so admin's own observer
    // doesn't fire a spurious self-eviction) and clean up the pending request entry.
    this.ydoc!.transact(() => {
      this.yAllowedUsers!.set(username!, asRole);
      if (yKey) this.yJoinRequests!.delete(yKey);
    }, LOCAL_ORIGIN);
    // Backup path A - awareness signal (fast, but can be lost on poor connections)
    this.awareness?.setLocalStateField('approvedUser', { username, asRole, ts: Date.now() });
    // Backup path B - write an approved- record into yJoinRequests WITHOUT LOCAL_ORIGIN
    // so the joiner's yJoinRequests.observe fires as a fallback even if awareness
    // delivery was missed.  The joiner's observer looks for this key to self-approve.
    const approvalTs = Date.now();
    this.ydoc!.transact(() => {
      this.yJoinRequests!.set(`approved-${username}`, { approved: true, asRole, ts: approvalTs } as any);
    }); // intentionally no LOCAL_ORIGIN - joiner must see this
    this.joinRequests$.next(this.joinRequests$.value.filter(r => r.username !== username));
  }

  rejectRequest(requestId: string): void {
    let username: string | null = null;
    if (requestId.startsWith('awareness-')) {
      username = requestId.slice('awareness-'.length);
    } else {
      const req = this.yJoinRequests?.get(requestId);
      if (req) username = req.username;
      this.yJoinRequests?.delete(requestId);
    }
    // Send a kick signal via awareness so the pending user's canvas is cleared
    // even though they were never formally admitted to the room.
    if (username) {
      this.joinRequests$.next(this.joinRequests$.value.filter(r => r.username !== username));
      this.awareness?.setLocalStateField('kick', { username, ts: Date.now() });
    }
  }

  kickUser(username: string): void {
    if (this.role$.value !== 'admin' || !this.yAllowedUsers) return;
    this.yAllowedUsers.delete(username);
    // Add to banned list so the kicked user cannot rejoin without admin unbanning them.
    if (this.yBannedUsers && this.ydoc) {
      this.ydoc.transact(() => {
        this.yBannedUsers!.set(username, Date.now().toString());
      }, LOCAL_ORIGIN);
    }
    this.awareness?.setLocalStateField('kick', { username, ts: Date.now() });
  }

  /** Remove a user from the banned list, allowing them to request to join again. */
  unbanUser(username: string): void {
    if (this.role$.value !== 'admin' || !this.yBannedUsers || !this.ydoc) return;
    this.ydoc.transact(() => { this.yBannedUsers!.delete(username); }, LOCAL_ORIGIN);
    // Clear the stale kick awareness signal so the unbanned user isn't auto-kicked
    // the moment they reconnect (awareness replays the signal with its original ts,
    // which is higher than the rejoining peer's reset _lastKickTs of 0).
    const currentKick = this.awareness?.getLocalState()?.['kick'] as { username: string; ts: number } | undefined;
    if (currentKick?.username === username) {
      this.awareness?.setLocalStateField('kick', null);
    }
  }

  promoteUser(username: string, newRole: 'admin'|'member'|'spectator'): void {
    if (this.role$.value !== 'admin' || !this.yAllowedUsers) return;
    // Room creator is permanently admin and cannot be demoted
    const creator = this.ySettings?.get('roomCreator') as string | undefined;
    if (creator && username === creator && newRole !== 'admin') return;
    this.yAllowedUsers.set(username, newRole);
    if (newRole === 'admin') this._appendSystemMessage(`${username} is now admin`);
  }

  setUserChatMuted(username: string, muted: boolean): void {
    if (this.role$.value !== 'admin' || !this.ydoc || !this.yChatMutedUsers) return;
    this.ydoc.transact(() => {
      if (muted) this.yChatMutedUsers!.set(username, Date.now().toString());
      else this.yChatMutedUsers!.delete(username);
    }, LOCAL_ORIGIN);
    this._appendSystemMessage(muted ? `${username} chat was disabled` : `${username} chat was enabled`);
  }

  closeRoomByAdmin(): void {
    if (this.role$.value !== 'admin') return;
    const creator = this.ySettings?.get('roomCreator') as string | undefined;
    if (!creator || creator !== this.localUser?.username) return;
    this._appendSystemMessage('Room closed by admin');
    this.awareness?.setLocalStateField('roomClosed', {
      by: this.localUser?.username ?? 'admin',
      ts: Date.now()
    });
    setTimeout(() => this.leaveRoom(), 250);
  }

  updateSettings(patch: Partial<CollabSettings>): void {
    if (!this.ySettings || this.role$.value !== 'admin') return;
    this.ydoc!.transact(() => {
      for (const [k, v] of Object.entries(patch)) this.ySettings!.set(k, v);
    }, LOCAL_ORIGIN);
  }

  /**
   * Broadcast one frame's pixels with a leading + trailing-edge throttle.
   *
   * Why throttle?  broadcastFrame is called on every mouse-move / paint event
   * (potentially 60 × /s).  Each call encodes 5 184 pixels to RLE, creates a
   * Yjs transaction, and fires a WebRTC send.  Without throttling this stacks
   * up CPU work and floods the data channel, causing perceptible lag with 90+
   * frames even though the data per call is small.
   *
   * Behaviour (30 ms window):
   *   • First call in a window → writes to Yjs immediately (leading edge).
   *   • Further calls within the window → only update the stored payload;
   *     a trailing-edge timer ensures the LAST state in the window is flushed.
   *   • On window expiry the timer fires and writes the latest pixels.
   * This gives ~33 fps network updates - imperceptible vs. P2P round-trip time
   * - while cutting Yjs transactions by up to 20×.
   */
  broadcastFrame(frameId: string, pixels: string[][], width: number, height: number): void {
    if (!this.yFrames || !this.ydoc || !this.can('draw')) return;

    const T = CollaborationService.BROADCAST_THROTTLE_MS;
    const now = Date.now();
    const last = this._lastBroadcastMs.get(frameId) ?? 0;
    const pending = this._broadcastThrottle.get(frameId);

    // Always keep the latest pixels so the trailing timer flushes fresh data
    if (pending) {
      pending.pixels = pixels;
      pending.width  = width;
      pending.height = height;
    }

    if (now - last >= T) {
      // ── Leading edge: enough time has passed - write immediately ─────────
      // Cancel any in-flight trailing timer (we're sending now)
      if (pending) { clearTimeout(pending.timer); this._broadcastThrottle.delete(frameId); }
      this._lastBroadcastMs.set(frameId, now);
      this._writeBroadcastFrame(frameId, pixels, width, height);

      // Schedule a trailing-edge flush in case more calls arrive in this window
      const timer = setTimeout(() => {
        const trail = this._broadcastThrottle.get(frameId);
        if (trail) {
          this._broadcastThrottle.delete(frameId);
          this._lastBroadcastMs.set(frameId, Date.now());
          this._writeBroadcastFrame(frameId, trail.pixels, trail.width, trail.height);
        }
      }, T);
      this._broadcastThrottle.set(frameId, { timer, pixels, width, height });

    } else if (!pending) {
      // ── Trailing edge: within throttle window, no timer yet - arm one ────
      const remaining = T - (now - last);
      const timer = setTimeout(() => {
        const trail = this._broadcastThrottle.get(frameId);
        if (trail) {
          this._broadcastThrottle.delete(frameId);
          this._lastBroadcastMs.set(frameId, Date.now());
          this._writeBroadcastFrame(frameId, trail.pixels, trail.width, trail.height);
        }
      }, remaining);
      this._broadcastThrottle.set(frameId, { timer, pixels, width, height });
    }
    // else: already have a trailing timer with updated pixels above - nothing more to do
  }

  /** Internal: the actual Yjs write for one frame. */
  private _writeBroadcastFrame(frameId: string, pixels: string[][], width: number, height: number): void {
    if (!this.yFrames || !this.ydoc) return;
    this.ydoc.transact(() => {
      let fm = this.yFrames!.get(frameId) as Y.Map<any> | undefined;
      if (!fm) {
        fm = new Y.Map<any>();
        this.yFrames!.set(frameId, fm);
      }
      fm.set('pd', this._encodePixels(pixels.flat()));
      fm.set('w', width);
      fm.set('h', height);
    }, LOCAL_ORIGIN);
  }

  broadcastFrameOrder(frameIds: string[]): void {
    if (!this.yFrameOrder || !this.ydoc) return;
    this.ydoc.transact(() => {
      this.yFrameOrder!.delete(0, this.yFrameOrder!.length);
      this.yFrameOrder!.insert(0, frameIds);
    }, LOCAL_ORIGIN);
  }

  broadcastFrameDelete(frameId: string): void {
    if (!this.yFrames || !this.ydoc) return;
    this.ydoc.transact(() => { this.yFrames!.delete(frameId); }, LOCAL_ORIGIN);
  }

  /**
   * Broadcast the full layer structure (order + properties + active layer ID) to all peers.
   * Stored as two keys in the shared yLayers map:
   *   - 'list': JSON-serialized SpriteLayer[]
   *   - 'activeLayerId': string | null
   */
  broadcastLayers(layers: any[], activeLayerId: string | null): void {
    if (!this.yLayers || !this.ydoc) return;
    this.ydoc.transact(() => {
      this.yLayers!.set('list', JSON.stringify(layers));
      this.yLayers!.set('activeLayerId', activeLayerId ?? '');
    }, LOCAL_ORIGIN);
  }

  /**
   * Broadcast ALL cels to peers — used after undo/redo/resize so peers get the
   * correct post-action cel state and can recomposite properly.
   */
  broadcastAllCels(cels: Record<string, string[][]>, width: number, height: number): void {
    if (!this.yCels || !this.ydoc) return;
    // Write updated cel values into yCels (LOCAL_ORIGIN so we don't re-receive)
    this.ydoc.transact(() => {
      for (const [key, pixels] of Object.entries(cels)) {
        if (!pixels || pixels.length === 0) continue;
        const h = pixels.length, w = pixels[0]?.length ?? width;
        let cm = this.yCels!.get(key) as Y.Map<any> | undefined;
        if (!cm) { cm = new Y.Map<any>(); this.yCels!.set(key, cm); }
        cm.set('pd', this._encodePixels(pixels.flat()));
        cm.set('w', w);
        cm.set('h', h);
      }
      // Remove stale cel keys that no longer exist after undo/redo
      const keepKeys = new Set(Object.keys(cels));
      const toDelete: string[] = [];
      this.yCels!.forEach((_: any, k: string) => { if (!keepKeys.has(k)) toDelete.push(k); });
      for (const k of toDelete) this.yCels!.delete(k);
    }, LOCAL_ORIGIN);
    // Re-push all cels without LOCAL_ORIGIN so peers' observeDeep fires
    this._resendAllCels();
  }

  /**
   * Broadcast a single cel's pixel data so peers can recomposite correctly.
   * Only the cel is sent — NOT a composite — so peers recomposite from their
   * full local cel state and the display is always correct.
   */
  broadcastCel(frameId: string, layerId: string, pixels: string[][], width: number, height: number): void {
    if (!this.yCels || !this.ydoc || !this.can('draw')) return;
    const key = `${frameId}|${layerId}`;
    const encoded = this._encodePixels(pixels.flat());
    this.ydoc.transact(() => {
      let cm = this.yCels!.get(key) as Y.Map<any> | undefined;
      if (!cm) {
        cm = new Y.Map<any>();
        this.yCels!.set(key, cm);
      }
      cm.set('pd', encoded);
      cm.set('w', width);
      cm.set('h', height);
    }, LOCAL_ORIGIN);
  }

  /**
   * Tell peers that a cel was cleared (e.g. collab undo reverted a layer to blank).
   * Deletes the key from yCels so Yjs propagates the deletion to all peers, who
   * then remove that cel from their local store and recomposite their frames.
   */
  broadcastCelClear(frameId: string, layerId: string): void {
    if (!this.yCels || !this.ydoc || !this.can('draw')) return;
    const key = `${frameId}|${layerId}`;
    if (!this.yCels.has(key)) return; // nothing in shared doc to clear
    this.ydoc.transact(() => {
      this.yCels!.delete(key);
    }, LOCAL_ORIGIN);
  }

  /**
   * Broadcast frame STRUCTURE changes (add / delete / reorder / tween) plus
   * the pixel data of any BRAND-NEW frames.
   *
   * Key optimisation: frames that already exist in the Yjs document keep their
   * current 'pd' value untouched.  Previously, this method re-encoded and
   * re-transmitted every frame's pixel data on every structural change, causing
   * 2-3 s delays with 90+ frames even though only the order changed.
   *
   * What gets written:
   *   • yFrameOrder  - always (tiny: N frame IDs)
   *   • New frames   - 'pd' + 'w' + 'h'  (frames whose ID is not yet in yFrames)
   *   • Existing frms - only 'w'/'h' if they changed (never 'pd')
   *
   * ⚠ Deletion is intentionally NOT done here.
   * Deleting frames inside broadcastAllFramesAtomic was the root cause of two
   * collab bugs with 3+ concurrent users:
   *   1. Race: Peer C broadcasts with its local frame set and deletes peer B's
   *      newly-added frame from Yjs because C hasn't received it yet.  B then
   *      re-broadcasts and deletes C's frame.  This ping-pong caused blank frames
   *      to accumulate and real drawings to be lost.
   *   2. Duplicate IDs: concurrent delete(0,n)+insert(0,ids) on the same YArray
   *      produces duplicate entries after Yjs CRDT merge, resulting in two store
   *      entries sharing the same frame ID - so deleting one deleted both.
   * Frame deletions are handled exclusively by broadcastFrameDelete() (called from
   * the collabBroadcastDelete$ effect, no debounce) and purgeOrphanedYFrames()
   * (called after undo/redo).
   */
  broadcastAllFramesAtomic(frames: {id:string;pixels:string[][];width:number;height:number}[]): void {
    if (!this.yFrames || !this.ydoc || !this.yFrameOrder) return;
    this.ydoc.transact(() => {
      // ── Add new frames / update metadata of existing frames ───────────────
      for (const frame of frames) {
        let fm = this.yFrames!.get(frame.id) as Y.Map<any> | undefined;
        if (!fm) {
          // Brand-new frame - write everything including pixel data
          fm = new Y.Map<any>();
          fm.set('pd', this._encodePixels(frame.pixels.flat()));
          fm.set('w', frame.width);
          fm.set('h', frame.height);
          this.yFrames!.set(frame.id, fm);
        } else {
          // Existing frame - only sync dimension changes; 'pd' is already in
          // sync from broadcastFrame calls during drawing.  Skipping 'pd' here
          // is the key perf fix: avoids re-encoding + re-transmitting N frames
          // worth of pixel data on every addFrame / deleteFrame / moveFrame.
          if (fm.get('w') !== frame.width)  fm.set('w', frame.width);
          if (fm.get('h') !== frame.height) fm.set('h', frame.height);
        }
      }

      // ── Update frame order (always) ────────────────────────────────────────
      this.yFrameOrder!.delete(0, this.yFrameOrder!.length);
      this.yFrameOrder!.insert(0, frames.map(f => f.id));
    }, LOCAL_ORIGIN);
  }

  /**
   * Explicitly remove from yFrames any frame IDs that are no longer in the
   * current local frame set.  Called after undo/redo to clean up frames that
   * were removed by undoing an addBlankFrame / appendFrames / etc.
   * Must NOT be called for structural changes (those use broadcastFrameDelete).
   */
  purgeOrphanedYFrames(currentFrameIds: string[]): void {
    if (!this.yFrames || !this.ydoc) return;
    const keepIds = new Set(currentFrameIds);
    const toDelete: string[] = [];
    this.yFrames.forEach((_, fid) => { if (!keepIds.has(fid)) toDelete.push(fid); });
    if (toDelete.length === 0) return;
    this.ydoc.transact(() => {
      for (const fid of toDelete) this.yFrames!.delete(fid);
    }, LOCAL_ORIGIN);
  }

  sendChatMessage(text: string): void {
    if (!this.yChat || !this.ydoc || !this.localUser || !text.trim()) return;
    if (this.yChatMutedUsers?.has(this.localUser.username)) {
      this.performanceWarning$.next('Chat is disabled for your user in this room.');
      return;
    }
    const msg: ChatMessage = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
      username: this.localUser.username,
      color: this.localUser.color,
      avatar: this.localUser.avatar,
      text: text.trim(),
      timestamp: Date.now(),
    };
    this.ydoc.transact(() => {
      // Keep the chat array capped at 200 messages to avoid unbounded growth
      if (this.yChat!.length >= 200) {
        this.yChat!.delete(0, this.yChat!.length - 199);
        this._lastChatLength = Math.max(0, this._lastChatLength - 1);
      }
      this.yChat!.push([msg]);
    });
  }

  postSystemMessage(text: string): void {
    if (!text.trim()) return;
    this._appendSystemMessage(text.trim());
  }

  private _appendSystemMessage(text: string): void {
    if (!this.yChat || !this.ydoc) return;
    const msg: ChatMessage = {
      id: `sys-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      username: 'System',
      color: '#8e9bff',
      avatar: 'ℹ',
      text,
      timestamp: Date.now(),
    };
    this.ydoc.transact(() => {
      if (this.yChat!.length >= 200) {
        this.yChat!.delete(0, this.yChat!.length - 199);
        this._lastChatLength = Math.max(0, this._lastChatLength - 1);
      }
      this.yChat!.push([msg]);
    });
  }

  updateLocalCursor(x: number, y: number, tool: string): void {
    if (!this.awareness || !this.localUser) return;
    this.awareness.setLocalStateField('cursor', { x, y, tool });
  }

  /** Broadcast which frame this user is currently viewing - shown in timeline */
  updateActiveFrame(frameId: string): void {
    if (!this.awareness) return;
    this.awareness.setLocalStateField('activeFrameId', frameId);
  }

  /**
   * Best-effort snapshot lookup used by remote frame-order handlers.
   * Helps avoid blank placeholder frames when order arrives before/without
   * a matching deep pixel-change event for newly inserted frame ids.
   */
  getFramePixelsSnapshot(frameId: string): { pixels: string[][]; width: number; height: number } | null {
    if (!this.yFrames) return null;
    const fm = this.yFrames.get(frameId) as Y.Map<any> | undefined;
    if (!fm) return null;
    const pd = fm.get('pd') as string | undefined;
    const w = fm.get('w') as number | undefined;
    const h = fm.get('h') as number | undefined;
    if (!pd || !w || !h) return null;
    return { pixels: this._decodePixels(pd, w, h), width: w, height: h };
  }

  copyToClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
    }
    const el = document.createElement('textarea');
    el.value = text; el.style.position = 'fixed'; el.style.opacity = '0';
    document.body.appendChild(el); el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return Promise.resolve(ok);
  }

  // ── RTCDataChannel chunking patch ────────────────────────────────────────────
  //
  // Chrome's RTCDataChannel.send() hard-limits individual messages to 256 KB.
  // y-webrtc sends the entire Yjs document state as a single binary message
  // (Y.encodeStateAsUpdate), which exceeds this limit for projects with many
  // frames and causes _channel.onerror → peer destroyed → reconnect loop.
  //
  // This patch intercepts RTCDataChannel.prototype ONCE at startup:
  //  • send()     - splits messages > MAX_CHUNK into 60 KB numbered packets
  //  • onmessage  - reassembles packets before delivering to y-webrtc
  //
  // Reliability: WebRTC data channels use SCTP (reliable + ordered delivery,
  // like TCP).  Individual IP packets are retransmitted automatically by SCTP -
  // the application layer never sees packet loss.  If the data channel CLOSES
  // (peer disconnect), our WeakMap buffers are GC-d, the peer reconnects, and
  // y-webrtc initiates a fresh sync which the patch handles transparently.
  //
  // Header format (12 bytes, prepended to every chunked packet):
  //   [0]    MAGIC    uint8   0xC7 - never the first byte of a y-webrtc message
  //   [1]    padding  uint8   0x00
  //   [2-3]  MSG_ID   uint16  LE - wrapping counter per large message
  //   [4-5]  IDX      uint16  LE - 0-based chunk index
  //   [6-7]  TOTAL    uint16  LE - total chunks in this message
  //   [8-11] SIZE     uint32  LE - original unchunked byte length

  private _patchRTCDataChannelForChunking(): void {
    if (CollaborationService._rtcChunkPatchApplied) return;
    CollaborationService._rtcChunkPatchApplied = true;

    const MAGIC    = 0xC7;
    const MAX_SEND = 60 * 1024; // 60 KB - safe below Chrome's 256 KB ceiling
    const HDR      = 12;

    // Per-channel reassembly: WeakMap → chunks are GC-d when channel closes
    const buffers = new WeakMap<RTCDataChannel, Map<number, (Uint8Array | null)[]>>();
    let msgSeq = 0; // global counter; wraps at 0xFFFF - fine for our throughput

    /** Attempt to reassemble a received buffer.
     *  Returns: reassembled ArrayBuffer   - message complete
     *           null                      - still waiting for more chunks
     *           the original ArrayBuffer  - not one of our packets (pass through) */
    const tryReassemble = (ch: RTCDataChannel, raw: ArrayBuffer): ArrayBuffer | null => {
      if (raw.byteLength < HDR) return raw;
      const v = new DataView(raw);
      if (v.getUint8(0) !== MAGIC) return raw;          // not our header → pass through
      const msgId = v.getUint16(2, true);
      const idx   = v.getUint16(4, true);
      const total = v.getUint16(6, true);
      const size  = v.getUint32(8, true);
      if (total === 0) return raw;                       // malformed - pass through

      let map = buffers.get(ch);
      if (!map) { map = new Map(); buffers.set(ch, map); }
      let chunks = map.get(msgId);
      if (!chunks) { chunks = new Array(total).fill(null); map.set(msgId, chunks); }
      chunks[idx] = new Uint8Array(raw, HDR);

      const received = chunks.reduce((n, c) => n + (c !== null ? 1 : 0), 0);
      if (received < total) return null;                 // still waiting

      // All chunks present - reassemble into one buffer
      map.delete(msgId);
      const out = new Uint8Array(size);
      let off = 0;
      for (const c of chunks) { out.set(c!, off); off += c!.byteLength; }
      return out.buffer;
    };

    // ── Patch send() ───────────────────────────────────────────────────────────
    const origSend = RTCDataChannel.prototype.send;
    (RTCDataChannel.prototype as any).send = function (data: any): void {
      // Normalise input to a plain ArrayBuffer
      let buf: ArrayBuffer;
      if (data instanceof ArrayBuffer) {
        buf = data;
      } else if (ArrayBuffer.isView(data)) {
        // Avoid a copy if the view spans the full underlying buffer
        buf = (data.byteOffset === 0 && data.byteLength === data.buffer.byteLength)
          ? (data.buffer as ArrayBuffer)
          : (data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer);
      } else {
        // String or Blob - pass unchanged (y-webrtc only sends binary)
        (origSend as Function).call(this, data);
        return;
      }

      if (buf.byteLength <= MAX_SEND) {
        (origSend as Function).call(this, buf);
        return;
      }

      // Split into MAX_SEND-byte chunks and send each individually
      msgSeq = (msgSeq + 1) & 0xFFFF;
      const myId  = msgSeq;
      const total = Math.ceil(buf.byteLength / MAX_SEND);
      for (let i = 0; i < total; i++) {
        const slice = buf.slice(i * MAX_SEND, Math.min((i + 1) * MAX_SEND, buf.byteLength));
        const pkt   = new Uint8Array(HDR + slice.byteLength);
        const dv    = new DataView(pkt.buffer);
        dv.setUint8 (0, MAGIC);
        dv.setUint8 (1, 0);
        dv.setUint16(2, myId,  true);
        dv.setUint16(4, i,     true);
        dv.setUint16(6, total, true);
        dv.setUint32(8, buf.byteLength, true);
        pkt.set(new Uint8Array(slice), HDR);
        (origSend as Function).call(this, pkt.buffer);
      }
    };

    // ── Patch onmessage setter (simple-peer sets channel.onmessage directly) ───
    const origDesc = Object.getOwnPropertyDescriptor(RTCDataChannel.prototype, 'onmessage');
    Object.defineProperty(RTCDataChannel.prototype, 'onmessage', {
      configurable: true,
      get(this: RTCDataChannel): ((ev: MessageEvent) => void) | null {
        return (this as any).__origOnMsg ?? null;
      },
      set(this: RTCDataChannel, handler: ((ev: MessageEvent) => void) | null) {
        (this as any).__origOnMsg = handler;
        if (!handler) { origDesc?.set?.call(this, null); return; }
        const ch = this;
        origDesc?.set?.call(this, (ev: MessageEvent) => {
          if (!(ev.data instanceof ArrayBuffer)) { handler(ev); return; }
          const out = tryReassemble(ch, ev.data);
          if (out === null) return; // still waiting for remaining chunks
          // Deliver original event unchanged if it wasn't chunked; otherwise
          // wrap the reassembled buffer in a synthetic MessageEvent
          handler(out === ev.data ? ev : new MessageEvent('message', { data: out }));
        });
      },
    });

  }

  // ── Pixel codec (RLE) ─────────────────────────────────────────────────────────
  //
  // Stores each frame's pixel array as a SINGLE compact string in Y.Map under
  // the key 'pd' (pixels-data).  This replaces the old Y.Array-of-5184-strings
  // approach whose Yjs-encoded size (~67 KB/frame × 30 frames ≈ 2 MB) exceeded
  // Chrome's 256 KB WebRTC data-channel message limit and caused _channel.onerror.
  //
  // Encoding: comma-separated runs.  Each run is either
  //   <compact>        - single pixel
  //   <compact>*<n>   - n consecutive identical pixels
  // where <compact> = "t" for "transparent", or the bare hex digits (no "#").
  //
  // Empty frame (all transparent, 54×96): "t*5184"  → 7 bytes ✓
  // Typical pixel-art frame (sparse): a few hundred bytes ✓
  // Dense full-color frame: ~30 KB worst-case - still under 256 KB ✓

  private _encodePixels(flat: string[]): string {
    if (!flat.length) return '';
    const parts: string[] = [];
    let i = 0;
    while (i < flat.length) {
      const color = flat[i];
      const c = (color === 'transparent' || color === '#00000000') ? 't' : color.slice(1); // strip '#'
      let n = 1;
      while (i + n < flat.length && flat[i + n] === color) n++;
      parts.push(n > 1 ? `${c}*${n}` : c);
      i += n;
    }
    return parts.join(',');
  }

  private _decodePixels(encoded: string, w: number, h: number): string[][] {
    const flat: string[] = [];
    if (encoded) {
      for (const part of encoded.split(',')) {
        const star = part.lastIndexOf('*');
        if (star !== -1) {
          const c = part.slice(0, star);
          const n = parseInt(part.slice(star + 1), 10);
          const color = c === 't' ? '#00000000' : '#' + c;
          for (let j = 0; j < n; j++) flat.push(color);
        } else {
          flat.push(part === 't' ? '#00000000' : '#' + part);
        }
      }
    }
    // Pad to full size in case of corrupt/partial data
    while (flat.length < w * h) flat.push('#00000000');
    const pixels: string[][] = [];
    for (let r = 0; r < h; r++) pixels.push(flat.slice(r * w, (r + 1) * w));
    return pixels;
  }

  // ── Private: connect ─────────────────────────────────────────────────────────

  private _connect(
    roomId: string, role: RoomRole,
    initialFrames: {id:string;pixels:string[][];width:number;height:number}[],
    initialSettings: CollabSettings|null, password: string,
    initialCels: Record<string, string[][]> = {},
    initialLayers: any[] = [],
    initialActiveLayerId: string | null = null
  ): void {
    this.connectionStatus$.next('connecting');
    this.roomId$.next(roomId);
    this.role$.next(role);

    this.ydoc        = new Y.Doc();
    this.yFrames     = this.ydoc.getMap<any>('frames');
    this.yFrameOrder = this.ydoc.getArray<string>('frameOrder');
    this.yLayers     = this.ydoc.getMap<any>('layerData');
    this.yCels       = this.ydoc.getMap<any>('celData');
    this.yJoinRequests = this.ydoc.getMap<any>('joinRequests');
    this.yAllowedUsers = this.ydoc.getMap<string>('allowedUsers');
    this.ySettings   = this.ydoc.getMap<any>('settings');
    this.yChat       = this.ydoc.getArray<any>('chat');
    this.yChatMutedUsers = this.ydoc.getMap<any>('chatMutedUsers');
    this.yBannedUsers = this.ydoc.getMap<any>('bannedUsers');
    this._lastChatLength = 0;

    if (role === 'admin' && initialSettings) {
      let restoredSettings = initialSettings;
      try {
        const saved = localStorage.getItem('pxlvd_collab_settings');
        if (saved) restoredSettings = { ...initialSettings, ...JSON.parse(saved) };
      } catch {}
      this.ydoc.transact(() => {
        for (const frame of initialFrames) {
          const fm = new Y.Map<any>();
          // Store pixels as compact RLE string ('pd') instead of Y.Array of
          // 5184 individual strings.  The old approach encoded to ~67 KB/frame
          // (×30 = ~2 MB), exceeding Chrome's 256 KB WebRTC message limit and
          // causing _channel.onerror on the joining peer.  RLE reduces a blank
          // frame to 7 bytes ("t*5184") and typical pixel art to a few KB.
          fm.set('pd', this._encodePixels(frame.pixels.flat()));
          fm.set('w', frame.width);
          fm.set('h', frame.height);
          this.yFrames!.set(frame.id, fm);
        }
        this.yFrameOrder!.insert(0, initialFrames.map(f => f.id));
        this.yAllowedUsers!.set(this.localUser!.username, 'admin');
        for (const [k, v] of Object.entries(restoredSettings)) this.ySettings!.set(k, v);
        // Permanently record who created this room - used to protect against demotion
        this.ySettings!.set('roomCreator', this.localUser!.username);
        // Write initial layer structure so joining members get the correct layers
        // immediately on full sync, even if the admin never modifies layers after
        // creating the room (which would be the only other trigger for broadcastLayers).
        if (initialLayers.length > 0) {
          this.yLayers!.set('list', JSON.stringify(initialLayers));
          this.yLayers!.set('activeLayerId', initialActiveLayerId ?? '');
        }
        // Write initial cel data so peers who join later can recomposite correctly
        for (const [key, pixels] of Object.entries(initialCels)) {
          if (!pixels || pixels.length === 0) continue;
          const h = pixels.length, w = pixels[0]?.length ?? 0;
          if (w === 0 || h === 0) continue;
          const cm = new Y.Map<any>();
          cm.set('pd', this._encodePixels(pixels.flat()));
          cm.set('w', w);
          cm.set('h', h);
          this.yCels!.set(key, cm);
        }
      }, LOCAL_ORIGIN);
    }

    const signalingUrl = SIGNALING_SERVERS[this._signalingIndex % SIGNALING_SERVERS.length];
    try {
      this.provider = new WebrtcProvider(`pxlvd-${roomId}`, this.ydoc, {
        signaling: [signalingUrl], maxConns: Math.min((initialSettings?.maxUsers ?? 12), MAX_USERS_HARD_CAP) - 1, filterBcConns: false, peerOpts: {}
      });
    } catch (err) {
      console.error('[Collab] WebRTC error', err);
      this._tryNextServer(roomId, role, initialFrames, initialSettings, password, initialCels, initialLayers, initialActiveLayerId);
      return;
    }

    this.awareness = this.provider.awareness;
    const u = this.localUser!;
    this.awareness.setLocalState({
      username: u.username, email: u.email, color: u.color, avatar: u.avatar,
      cursor: { x: -1, y: -1, tool: 'draw' },
      activeFrameId: null,
      role,
      pendingJoin: role === 'pending',
      pixelFormatVersion: PIXEL_FORMAT_VERSION
    });

    if (role === 'pending') {
      // Broadcast join request repeatedly until approved or cancelled.
      // Use a local flag so the loop stops the moment this connection is torn down.
      let broadcastActive = true;
      const stopBroadcast = () => { broadcastActive = false; };
      // Register a one-time teardown hook so the loop stops on leaveRoom/_teardown
      this._stopPendingBroadcast = stopBroadcast;

      const broadcastRequest = () => {
        if (!broadcastActive || this.role$.value !== 'pending') return;
        this.awareness?.setLocalStateField('pendingJoin', true);
        this.awareness?.setLocalStateField('joinRequest', {
          username: u.username, email: u.email, color: u.color, avatar: u.avatar,
          password, ts: Date.now()
        });
        setTimeout(() => broadcastRequest(), 3000);
      };
      setTimeout(() => broadcastRequest(), 500);

      // Active approval polling - reads all peer awareness states every 1.5 s.
      // Catches approvedUser signals that the reactive observer may have missed.
      this._pollForApproval();
    }

    if (role === 'admin') {
      this._wasInRoom = true;
      this.isInRoom$.next(true);
      this.collabHydrated$.next(true);
      this.syncing$.next(false);
      this._clearRejoinPolicyContext();
    }

    // ── Allowed users (approve / kick) ────────────────────────────────────────
    this.yAllowedUsers!.observe(() => {
      let myRole = this.yAllowedUsers!.get(this.localUser!.username) as RoomRole | undefined;
      if (this.isInRoom$.value && myRole === undefined) {
        // Creator-admin refresh can transiently observe an empty allowedUsers map.
        // Self-heal by restoring the creator's admin entry instead of leaving.
        const roomCreator = this.ySettings?.get('roomCreator') as string | undefined;
        const isCreatorAdmin = this.role$.value === 'admin'
          && !!this.localUser?.username
          && roomCreator === this.localUser.username;
        if (isCreatorAdmin) {
          try { this.yAllowedUsers!.set(this.localUser!.username, 'admin'); } catch {}
          this._missingAllowedRoleSinceMs = 0;
          return;
        }
        // Suppress eviction if we're in the middle of a rejoin: the Yjs doc hasn't synced
        // yet so allowedUsers appears empty. Once synced, the observer fires again.
        if (this._approvedViaAwareness || this._isRejoin) return;
        if (!this._missingAllowedRoleSinceMs) {
          this._missingAllowedRoleSinceMs = Date.now();
          return;
        }
        if (Date.now() - this._missingAllowedRoleSinceMs < 8000) return;
        this._missingAllowedRoleSinceMs = 0;
        this.zone.run(() => this.leaveRoom());
        return;
      }
      if (myRole && (!this.isInRoom$.value || myRole !== this.role$.value)) {
        this._missingAllowedRoleSinceMs = 0;
        // If auto-rejoin is disabled, a refreshed non-admin must remain pending
        // until explicit admin approval, even if a stale yAllowedUsers entry exists.
        if (this._shouldForcePendingOnRejoin(myRole) && !this.isInRoom$.value) {
          this.awareness?.setLocalStateField('pendingJoin', true);
          return;
        }
        // Non-creator users must never auto-upgrade to admin from a stale yAllowedUsers entry.
        // The room creator is always allowed to recover admin role on rejoin.
        if (!this._isCreatorRejoin && !this._isLocalRoomCreator() && myRole === 'admin' && !this.isInRoom$.value) {
          myRole = 'member' as RoomRole;
          try { this.yAllowedUsers!.set(this.localUser!.username, 'member'); } catch {}
        }
        this.zone.run(() => {
          this.role$.next(myRole as RoomRole);
          this.awareness?.setLocalStateField('role', myRole);
          this.awareness?.setLocalStateField('pendingJoin', false);
          if (!this.isInRoom$.value) {
            this._wasInRoom = true;
            // Arm the eviction guard so a subsequent yAllowedUsers.observe firing
            // with myRole===undefined (e.g. delayed Yjs re-sync) does NOT kick us.
            this._approvedViaAwareness = true;
            this._clearRejoinPolicyContext();
            this.isInRoom$.next(true);
            if (myRole === 'admin' && this._joinedViaRequestJoin) {
              // Rejoining admin: they need to receive the current room state — they
              // are no longer the authoritative source.  Do NOT mark as hydrated yet;
              // wait for _emitFullSync() to deliver the correct frames first.
              //
              // Crucially, yAllowedUsers.observe can fire from the local IndexedDB
              // cache BEFORE WebRTC has delivered the current room state.  If we call
              // _emitFullSync() here we may read stale frames and set
              // _fullSyncEmitted=true, which then blocks the correct WebRTC-synced
              // emission from the 'synced' event.
              //
              // Strategy:
              //   • _providerSynced=true  → WebRTC sync already done, data is current
              //                             → call _emitFullSync() now.
              //   • _providerSynced=false → Still syncing from IndexedDB/WebRTC.
              //                             → Skip here; the 'synced' handler will call
              //                               _emitFullSync() with up-to-date data.
              this.collabHydrated$.next(false);
              this.syncing$.next(true);
              this._fullSyncEmitted = false;
              if (this._providerSynced) {
                // WebRTC sync already completed before role resolved — safe to emit now.
                this._emitFullSync();
              }
              // else: 'synced' event will reset _fullSyncEmitted and call _emitFullSync().
            } else if (myRole === 'admin') {
              // Original room creator joining via createRoom() — they ARE authoritative.
              this.collabHydrated$.next(true);
              this.syncing$.next(false);
            } else {
              this.collabHydrated$.next(false);
              this.syncing$.next(true);
            }
            this.persistLastRoom(myRole as RoomRole);
            // Reset guard so any earlier premature emission (from synced while pending) doesn't block the approval sync
            if (myRole !== 'admin' || !this._joinedViaRequestJoin) {
              // For non-rejoining-admin roles: reset and emit as usual.
              // For rejoining admin: already handled above (conditional on _providerSynced).
              this._fullSyncEmitted = false;
              this._emitFullSync();
            }
          }
        });
      }
    });

    // ── Settings ──────────────────────────────────────────────────────────────
    this.ySettings!.observe(() => {
      const s: any = {};
      this.ySettings!.forEach((v, k) => { s[k] = v; });
      // Extract roomCreator (not part of CollabSettings - stored alongside them)
      const creator = s['roomCreator'] as string | undefined;
      if (creator) this.zone.run(() => this.roomCreator$.next(creator));
      const merged = { ...DEFAULT_COLLAB_SETTINGS, ...s };
      this.zone.run(() => this.collabSettings$.next(merged));
      // During refresh rejoin, do not trust defaults. Resolve autoRejoin only from synced room settings.
      if (this._rejoinPolicyActive && !this.isInRoom$.value && this.role$.value === 'pending') {
        const prev = this._rejoinAutoRejoinEnabled;
        this._rejoinAutoRejoinEnabled = merged.autoRejoin;
        // As soon as room settings resolve, re-run approval evaluation immediately.
        // This avoids waiting for unrelated peer/awareness events on member refresh.
        if (prev !== this._rejoinAutoRejoinEnabled) {
          this._recheckApproval();
        }
        // If auto-rejoin is disabled, ensure a pending request record exists so admin sees it.
        if (this._rejoinAutoRejoinEnabled === false && this.localUser && this.yJoinRequests && this.ydoc) {
          const u = this.localUser;
          const reqKey = `req-${u.username}`;
          this.ydoc.transact(() => {
            this.yJoinRequests!.set(reqKey, {
              id: reqKey,
              username: u.username,
              email: u.email,
              color: u.color,
              avatar: u.avatar,
              password: this.collabSettings$.value.roomPassword ?? '',
              timestamp: Date.now()
            });
          });
        }
      }
      if (this.role$.value === 'admin') {
        try { localStorage.setItem('pxlvd_collab_settings', JSON.stringify(merged)); } catch {}
      }
    });

    // ── Frame deletes ─────────────────────────────────────────────────────────
    this.yFrames!.observe(event => {
      if (!this._canReceiveIncrementalRoomUpdates()) return;
      if (event.transaction.origin === LOCAL_ORIGIN) return;
      event.changes.keys.forEach((change, frameId) => {
        if (change.action === 'delete') {
          this.zone.run(() => this.remoteFrameDelete$.next(frameId));
        }
      });
    });

    // ── Pixel changes - skip transactions we originated ───────────────────────
    this.yFrames!.observeDeep(events => {
      if (!this._canReceiveIncrementalRoomUpdates()) return;
      // If every event in this batch came from us, ignore entirely
      if (events.every(e => e.transaction.origin === LOCAL_ORIGIN)) return;

      const changedFrameIds = new Set<string>();
      for (const event of events) {
        if (event.transaction.origin === LOCAL_ORIGIN) continue;
        const frameId = event.path[0] as string | undefined;
        if (frameId && typeof frameId === 'string') changedFrameIds.add(frameId);
      }

      changedFrameIds.forEach(frameId => {
        const fm = this.yFrames!.get(frameId) as Y.Map<any>;
        if (!fm) return;
        const pd  = fm.get('pd') as string | undefined;
        const w   = fm.get('w') as number;
        const h   = fm.get('h') as number;
        if (pd === undefined || !w || !h) return;
        const pixels = this._decodePixels(pd, w, h);
        this.zone.run(() => this.remotePixelChange$.next({ frameId, pixels }));
      });
    });

    // ── Frame order ───────────────────────────────────────────────────────────
    this.yFrameOrder!.observe(event => {
      if (!this._canReceiveIncrementalRoomUpdates()) return;
      if (event.transaction.origin === LOCAL_ORIGIN) return;
      this.zone.run(() => this.remoteFrameOrder$.next(this.yFrameOrder!.toArray()));
    });

    // ── Layer structure ───────────────────────────────────────────────────────
    this.yLayers!.observe(event => {
      if (!this._canReceiveIncrementalRoomUpdates()) return;
      if (event.transaction.origin === LOCAL_ORIGIN) return;
      const raw = this.yLayers!.get('list') as string | undefined;
      const activeLayerId = (this.yLayers!.get('activeLayerId') as string | undefined) || null;
      if (!raw) return;
      try {
        const layers = JSON.parse(raw);
        this.zone.run(() => this.remoteLayerSync$.next({ layers, activeLayerId }));
      } catch {}
    });

    // ── Cel data (per-layer pixel data for correct recompositing) ────────────
    this.yCels!.observeDeep(events => {
      if (!this._canReceiveIncrementalRoomUpdates()) return;
      if (events.every(e => e.transaction.origin === LOCAL_ORIGIN)) return;

      const changedKeys = new Set<string>();
      const deletedKeys = new Set<string>();

      for (const event of events) {
        if (event.transaction.origin === LOCAL_ORIGIN) continue;

        if (event.path.length === 0) {
          // Event on yCels itself — a top-level key was added or deleted.
          // event.changes.keys: Map<string, { action: 'add'|'update'|'delete' }>
          const mapEvent = event as Y.YMapEvent<any>;
          mapEvent.changes.keys.forEach((change, key) => {
            if (change.action === 'delete') {
              deletedKeys.add(key);
            } else {
              // 'add' — a brand-new cel Y.Map was inserted (the nested observe
              // will fire separately with its content, so just track it here too)
              changedKeys.add(key);
            }
          });
        } else {
          // Nested event: a value inside a cel's Y.Map changed (e.g. 'pd' updated)
          const key = event.path[0] as string | undefined;
          if (key && typeof key === 'string') changedKeys.add(key);
        }
      }

      // Decode updated cels (skip any that were also deleted — deletion wins)
      const cels: Record<string, string[][]> = {};
      changedKeys.forEach(key => {
        if (deletedKeys.has(key)) return; // deleted in same transaction, skip
        const cm = this.yCels!.get(key) as Y.Map<any> | undefined;
        if (!cm) {
          // Key is gone even though we didn't see a delete event — treat as deleted
          deletedKeys.add(key);
          return;
        }
        const pd = cm.get('pd') as string | undefined;
        const w  = cm.get('w') as number;
        const h  = cm.get('h') as number;
        if (pd === undefined || !w || !h) return;
        cels[key] = this._decodePixels(pd, w, h);
      });

      if (Object.keys(cels).length > 0) {
        this.zone.run(() => this.remoteCelChange$.next({ cels }));
      }
      if (deletedKeys.size > 0) {
        this.zone.run(() => this.remoteCelClear$.next({ celKeys: Array.from(deletedKeys) }));
      }
    });

    // ── Join requests ─────────────────────────────────────────────────────────
    const emitJoinRequests = () => {
      const reqs: JoinRequest[] = [];
      this.yJoinRequests!.forEach((v: JoinRequest, k: string) => {
        // Skip backup approval records (approved-<username>) - not real join requests
        if (k.startsWith('approved-')) return;
        // Skip users already in yAllowedUsers only when auto-rejoin is enabled.
        // When auto-rejoin is disabled, refreshed users must still be manually approved.
        if (this.yAllowedUsers?.has(v.username) && this.collabSettings$.value.autoRejoin) return;
        reqs.push(v);
      });
      const pwd = this.collabSettings$.value.roomPassword;
      const filtered = pwd ? reqs.filter(r => r.password === pwd) : reqs;
      // Merge with existing awareness-based entries to avoid a race where
      // this observer fires just after the awareness handler populated the list.
      this.zone.run(() => {
        const existing = this.joinRequests$.value.filter(r => r.id.startsWith('awareness-'));
        const yjsNames = new Set(filtered.map(r => r.username));
        this.joinRequests$.next([
          ...filtered,
          ...existing.filter(r => !yjsNames.has(r.username))
        ]);
      });
    };

    // Also check for approved- backup records written by the admin.
    // This is the joiner-side observer: if we see approved-<ourUsername> in the map,
    // we self-approve via the Yjs fallback path.
    this.yJoinRequests!.observe(() => {
      if (this.role$.value === 'pending' && this.localUser && !this.isInRoom$.value) {
        const approvalKey = `approved-${this.localUser.username}`;
        const record = this.yJoinRequests!.get(approvalKey) as any;
        if (record?.approved) {
          const approvalTs = (record.ts as number) ?? 0;
          if (this._pendingJoinStartedAt && approvalTs < this._pendingJoinStartedAt) return;
          if (approvalTs > this._lastApprovalTs) {
            this._lastApprovalTs = approvalTs;
            const approvedRole = record.asRole as RoomRole;
            this._approvedViaAwareness = true;
            if (this._stopPendingBroadcast) { this._stopPendingBroadcast(); this._stopPendingBroadcast = null; }
            if (this._stopPollApproval)     { this._stopPollApproval();     this._stopPollApproval = null; }
            this.zone.run(() => {
              this.role$.next(approvedRole);
              this.awareness?.setLocalStateField('role', approvedRole);
              this.awareness?.setLocalStateField('pendingJoin', false);
              this._wasInRoom = true;
              this._clearRejoinPolicyContext();
              this.isInRoom$.next(true);
              if (approvedRole === 'admin') {
                this.collabHydrated$.next(true);
                this.syncing$.next(false);
              } else {
                this.collabHydrated$.next(false);
                this.syncing$.next(true);
              }
              this.persistLastRoom(approvedRole);
              // Reset guard so any premature emission while still pending doesn't block the approval sync
              this._fullSyncEmitted = false;
              this._emitFullSync();
            });
            return; // approved - no need to also emit join requests
          }
        }
      }
      emitJoinRequests();
    });
    this.isInRoom$.pipe(filter(v => v === true), take(1)).subscribe(() => emitJoinRequests());

    // ── Chat messages (ephemeral – stored in IDB client-side only) ────────────
    this.yChat!.observe(event => {
      const arr = this.yChat!.toArray();
      const newItems = arr.slice(this._lastChatLength);
      // Always advance the length counter so we don't re-emit on the next observe.
      this._lastChatLength = arr.length;
      // Suppress any messages that arrived before the initial Yjs sync completes.
      // y-webrtc merges the peer's full yChat array into our doc before firing
      // 'synced', which means observe fires with ALL historical messages before
      // _chatSynced is set.  Without this guard every old message would re-emit
      // as a new chat bubble for every fresh connection.
      if (!this._chatSynced) return;
      newItems.forEach((msg: ChatMessage) => {
        this.zone.run(() => this.chatMessage$.next(msg));
      });
    });

    // ── Banned users list ─────────────────────────────────────────────────────
    this.yBannedUsers!.observe(() => {
      const banned: string[] = [];
      this.yBannedUsers!.forEach((_: any, username: string) => banned.push(username));
      this.zone.run(() => this.bannedUsers$.next(banned));
    });
    this.yChatMutedUsers!.observe(() => {
      const muted: string[] = [];
      this.yChatMutedUsers!.forEach((_: any, username: string) => muted.push(username));
      this.zone.run(() => this.chatMutedUsers$.next(muted));
    });

    // ── Awareness: cursors, peer frame positions, join requests ───────────────
    this.awareness!.on('change', () => {
      const states  = this.awareness!.getStates();
      const localId = this.ydoc!.clientID;
      const peers: PeerState[]       = [];
      const pendingFromAwareness: JoinRequest[] = [];
      const activeRemoteUsers = new Set<string>();
      let pixelFormatMismatchDetected = false;
      const mismatchedPeerUsernames = new Set<string>();

      // ── Non-creator admin departure detection (creator-admin only) ──────────
      // When the local user is the room creator, check if any previously-tracked
      // non-creator admin has gone offline.  If so, demote them to member so they
      // must re-earn admin status if they return.
      if (this.role$.value === 'admin' && this.localUser) {
        const creator = this.ySettings?.get('roomCreator') as string | undefined;
        if (creator === this.localUser.username) {
          const currentClientIds = new Set(states.keys());
          for (const [cid, username] of this._onlineNonCreatorAdmins) {
            if (!currentClientIds.has(cid)) {
              this._onlineNonCreatorAdmins.delete(cid);
              const currentRole = this.yAllowedUsers?.get(username) as RoomRole | undefined;
              if (currentRole === 'admin' && this.yAllowedUsers && this.ydoc) {
                try { this.yAllowedUsers.set(username, 'member'); } catch {}
              }
            }
          }
        }
      }

      states.forEach((state: any, clientId: number) => {
        if (clientId === localId || !state?.username) return;

        if (state?.roomClosed?.ts) {
          const closedTs = (state.roomClosed.ts as number) ?? 0;
          const closedBy = state.roomClosed.by as string | undefined;
          const isSelfCloseSignal = !!closedBy && closedBy === this.localUser?.username;
          if (closedTs > this._lastRoomClosedTs && !isSelfCloseSignal) {
            this._lastRoomClosedTs = closedTs;
            this.zone.run(() => this.performanceWarning$.next('Room closed by admin.'));
            setTimeout(() => {
              const shouldClose = this.isInRoom$.value || this.role$.value === 'pending' || this.role$.value === 'admin';
              if (shouldClose) this.leaveRoom();
            }, 650);
          }
        }

        if (state?.kick?.username === this.localUser?.username) {
          // Deduplicate: awareness can replay the same kick state on reconnect/resync.
          // Only process each unique kick event once by tracking its timestamp.
          const kickTs = (state.kick.ts as number) ?? 0;
          if (kickTs <= this._lastKickTs) return;
          this._lastKickTs = kickTs;
          // Kicked or rejected - wipe regardless of whether we were formally in the room
          this.zone.run(() => {
            localStorage.removeItem('pxlvd_collab_last');
            this._teardown();
            this.isInRoom$.next(false);
            this.role$.next('none');
            this.roomId$.next(null);
            this.peers$.next([]);
            this.joinRequests$.next([]);
            this.connectionStatus$.next('disconnected');
            this.collabSettings$.next({ ...DEFAULT_COLLAB_SETTINGS });
            // Always wipe canvas when kicked/rejected so no data leaks to unauthorised users
            this.canvasWipe$.next();
          });
          return;
        }

        if (state?.approvedUser?.username === this.localUser?.username && this.role$.value === 'pending') {
          const approvalTs = state.approvedUser.ts as number ?? 0;
          if (this._pendingJoinStartedAt && approvalTs < this._pendingJoinStartedAt) return;
          // Only process each unique approval event once - ts changes each time admin approves
          if (approvalTs <= this._lastApprovalTs) return;
          this._lastApprovalTs = approvalTs;
          const approvedRole = state.approvedUser.asRole as RoomRole;
          this._approvedViaAwareness = true;
          // Stop the pending broadcast loop immediately
          if (this._stopPendingBroadcast) { this._stopPendingBroadcast(); this._stopPendingBroadcast = null; }
          this.zone.run(() => {
            this.role$.next(approvedRole);
            this.awareness?.setLocalStateField('role', approvedRole);
            this.awareness?.setLocalStateField('pendingJoin', false);
            this._wasInRoom = true;
            this._clearRejoinPolicyContext();
            this.isInRoom$.next(true);
            this.persistLastRoom(approvedRole);
            // Reset guard so any premature emission while still pending doesn't block the approval sync
            this._fullSyncEmitted = false;
            this._emitFullSync();
          });
          return;
        }

        if (state.pendingJoin || state.role === 'pending') {
          const hasAllowedEntry = this.yAllowedUsers?.has(state.username) ?? false;
          if (!hasAllowedEntry || !this.collabSettings$.value.autoRejoin) {
            // Auto-kick banned users - they are not allowed to rejoin until unbanned
            if (this.yBannedUsers?.has(state.username) && this.role$.value === 'admin') {
              this.awareness?.setLocalStateField('kick', { username: state.username, ts: Date.now() });
              return;
            }
            pendingFromAwareness.push({
              id: `awareness-${state.username}`,
              username: state.username,
              email:    state.joinRequest?.email    ?? state.email    ?? '',
              color:    state.joinRequest?.color    ?? state.color    ?? '#ccc',
              avatar:   state.joinRequest?.avatar   ?? state.avatar   ?? '?',
              password: state.joinRequest?.password ?? '',
              timestamp: state.joinRequest?.ts ?? Date.now()
            });
          }
          return;
        }

        if (state.role !== 'none' && state.role !== 'pending') {
          activeRemoteUsers.add(state.username);
        }

        const peerPixelFormatVersion = (state.pixelFormatVersion as number | undefined) ?? 1;
        if (peerPixelFormatVersion !== PIXEL_FORMAT_VERSION) {
          pixelFormatMismatchDetected = true;
          mismatchedPeerUsernames.add(state.username);
        }

        // Track non-creator admins for departure detection (creator only)
        if (state.role === 'admin') {
          const creator = this.ySettings?.get('roomCreator') as string | undefined;
          if (creator && state.username !== creator) {
            this._onlineNonCreatorAdmins.set(clientId, state.username);
          }
        } else {
          this._onlineNonCreatorAdmins.delete(clientId);
        }

        peers.push({
          clientId,
          username:      state.username,
          color:         state.color         ?? '#fff',
          avatar:        state.avatar        ?? '?',
          cursorX:       state.cursor?.x     ?? -1,
          cursorY:       state.cursor?.y     ?? -1,
          activeTool:    state.cursor?.tool  ?? 'draw',
          activeFrameId: state.activeFrameId ?? null,
          role:          state.role          ?? 'member',
          isOnline:      true,
          pixelFormatVersion: peerPixelFormatVersion
        });
      });

      if (this.role$.value === 'admin' && this.localUser) {
        const creator = this.ySettings?.get('roomCreator') as string | undefined;
        const isCreatorAdmin = creator === this.localUser.username;
        if (isCreatorAdmin) {
          if (!this._presenceInitialized) {
            this._presenceInitialized = true;
            this._lastActiveRemoteUsers = activeRemoteUsers;
          } else {
            const joined = [...activeRemoteUsers].filter(u => !this._lastActiveRemoteUsers.has(u));
            const left = [...this._lastActiveRemoteUsers].filter(u => !activeRemoteUsers.has(u));
            joined.forEach(u => this._appendSystemMessage(`${u} joined the room`));
            left.forEach(u => this._appendSystemMessage(`${u} left the room`));
            this._lastActiveRemoteUsers = activeRemoteUsers;
          }
        }
      }

      this.zone.run(() => {
        this.peers$.next(peers);
        this._checkPerformanceWarning(peers.length + 1, pixelFormatMismatchDetected);
        if (pixelFormatMismatchDetected) {
          this._handlePixelFormatMismatch([...mismatchedPeerUsernames]);
        } else {
          this._pixelFormatMismatchHandled = false;
        }
        const yjsReqs        = this.joinRequests$.value.filter(r => !r.id.startsWith('awareness-'));
        const awarenessNames = new Set(pendingFromAwareness.map(r => r.username));
        this.joinRequests$.next([
          ...pendingFromAwareness,
          ...yjsReqs.filter(r => !awarenessNames.has(r.username))
        ]);
      });
    });

    this.provider!.on('synced', ({ synced }: { synced: boolean }) => this.zone.run(() => {
      this.connectionStatus$.next('connected');
      // Keep refresh-policy context active until role resolution. Transport sync and
      // rejoin-policy completion are separate events.
      // Mark chat as synced - from this point on yChat.observe is allowed to emit.
      // Also re-anchor _lastChatLength to the fully-synced length so no pre-sync
      // messages are replayed (the observe callback already tracks this, but a
      // belt-and-suspenders reset here prevents any off-by-one edge cases).
      this._chatSynced = true;
      this._lastChatLength = this.yChat?.length ?? 0;
      // Mark that the provider has delivered the current network state.  From this
      // point any read of yFrames/yCels/yLayers reflects the authoritative room data,
      // not just the local IndexedDB cache.
      this._providerSynced = true;
      if (this.role$.value !== 'admin' || this._joinedViaRequestJoin) {
        // Pending user: persist join request to Yjs map now that the doc is synced.
        // This makes the admin see the request even if awareness timing was off.
        if (this.role$.value === 'pending' && this.localUser && this.yJoinRequests) {
          const u = this.localUser;
          const reqKey = `req-${u.username}`;
          const approvalKey = `approved-${u.username}`;
          this.ydoc!.transact(() => {
            // Drop stale approval breadcrumbs from previous sessions so this refresh
            // attempt cannot auto-accept unless the admin approves again.
            if (this.yJoinRequests!.has(approvalKey)) {
              this.yJoinRequests!.delete(approvalKey);
            }
            const hasAllowedEntry = this.yAllowedUsers?.has(u.username) ?? false;
            const canAutoRejoin = this._rejoinPolicyActive && this._rejoinAutoRejoinEnabled === true;
            const shouldWritePendingRequest = !hasAllowedEntry || !canAutoRejoin;
            // When autoRejoin is disabled, write pending request even if stale
            // allowedUsers entry exists.
            if (shouldWritePendingRequest) {
              this.yJoinRequests!.set(reqKey, {
                id: reqKey, username: u.username, email: u.email,
                color: u.color, avatar: u.avatar, password,
                timestamp: Date.now()
              });
            }
          }); // No LOCAL_ORIGIN - admin's yJoinRequests.observe must fire
        }
        // Check if we got approved while syncing (Yjs may already have our entry)
        this._recheckApproval();
        // Do NOT emit full sync here for pending users - the Yjs doc may already
        // contain the room's full frame data, which would set _fullSyncEmitted = true
        // prematurely and block the approval-triggered sync from firing later.
        // Full sync is emitted from the approval handlers once the user is actually in the room.
        if (this.role$.value !== 'pending') {
          // For a rejoining admin whose role was already resolved from IndexedDB
          // (yAllowedUsers.observe fired before synced), _emitFullSync() may have
          // already run with stale data and set _fullSyncEmitted=true.  Now that
          // WebRTC sync is complete the Yjs doc is authoritative — reset the guard
          // and re-emit so the store gets the current room state.
          if (this._joinedViaRequestJoin && this.role$.value === 'admin') {
            this._fullSyncEmitted = false;
          }
          this._emitFullSync();
        }
      }
      // NOTE: Admin no longer calls _adminBroadcastAllFrames on synced.
      // y-webrtc's document sync protocol delivers the full Yjs state to newly
      // connected peers automatically.  An explicit re-broadcast would create a
      // duplicate ~900 KB transaction that risks overwhelming the WebRTC channel.
    }));

    this.provider!.on('peers', ({ webrtcPeers, bcPeers }: any) => this.zone.run(() => {
      if (this.connectionStatus$.value !== 'connected') this.connectionStatus$.next('connected');
      if (this.isInRoom$.value && this.role$.value !== 'admin') {
        this._emitFullSync();
      } else if (this.isInRoom$.value && this.role$.value === 'admin' && this._joinedViaRequestJoin) {
        // Rejoining admin: emit full sync as a safety net.  _emitFullSync() is
        // idempotent (_fullSyncEmitted guards), so calling it here is safe even if
        // yAllowedUsers.observe already triggered it.  This handles the race where
        // the peers event fires after role resolution but _emitFullSync() was blocked.
        this._emitFullSync();
      } else if (this.role$.value === 'pending') {
        // New peer appeared while we're still pending - re-check for approval in case
        // the approving admin just connected and their yAllowedUsers write is now visible.
        this._recheckApproval();
      }
      // Admin: if NEW peers appeared, broadcast frames in small chunks.
      // y-webrtc's initial doc sync can fail silently for large documents
      // (>256 KB exceeds WebRTC SCTP message-size limit).  The chunked
      // re-broadcast compensates by sending 2 frames at a time.
      if (this.role$.value === 'admin' && this.yFrames && this.yFrames.size > 0) {
        const allPeers = [...(webrtcPeers ?? []), ...(bcPeers ?? [])];
        const hasNewPeer = allPeers.some((p: string) => !this._knownPeers.has(p));
        if (hasNewPeer) {
          allPeers.forEach((p: string) => this._knownPeers.add(p));
          this._adminBroadcastFramesChunked();
        }
      }
    }));
  }

  private _tryNextServer(
    roomId: string, role: RoomRole,
    frames: {id:string;pixels:string[][];width:number;height:number}[],
    settings: CollabSettings|null, password: string,
    cels: Record<string, string[][]> = {},
    layers: any[] = [],
    activeLayerId: string | null = null
  ): void {
    this._signalingIndex++;
    if (this._signalingIndex < SIGNALING_SERVERS.length * 2) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = setTimeout(() => this._connect(roomId, role, frames, settings, password, cels, layers, activeLayerId), 2000);
    } else {
      this.zone.run(() => this.connectionStatus$.next('disconnected'));
    }
  }

  private _checkPerformanceWarning(n: number, pixelFormatMismatch = false): void {
    if (pixelFormatMismatch) {
      this.performanceWarning$.next('⚠ Some collaborators are on an older pixel format. Ask everyone to refresh to avoid black/transparent mismatches.');
      return;
    }
    const maxUsers = Math.min(this.collabSettings$.value.maxUsers || 12, MAX_USERS_HARD_CAP);
    if (n >= maxUsers) this.performanceWarning$.next(`⚠ Room at max capacity (${maxUsers}).`);
    else if (n >= WARN_THRESHOLD) this.performanceWarning$.next(`⚠ ${n} users - performance may degrade.`);
    else this.performanceWarning$.next(null);
  }

  private _canReceiveIncrementalRoomUpdates(): boolean {
    if (!this.isInRoom$.value) return false;
    const role = this.role$.value;
    if (role === 'admin') return true;
    if (role === 'pending' || role === 'none') return false;
    return this.collabHydrated$.value;
  }

  private _canReceiveFullRoomSync(): boolean {
    if (!this.isInRoom$.value) return false;
    const role = this.role$.value;
    // An admin who created the room via createRoom() IS the authoritative source of
    // truth for the Yjs document — they wrote the initial state and must never
    // overwrite it with a sync from themselves.  Skip full sync for them.
    //
    // However, an admin who entered via requestJoin() (e.g. the room creator rejoining
    // after closing the tab, or any promoted admin reconnecting) is NOT the authority.
    // The Yjs document was updated by other peers while they were away.  They MUST
    // receive a full sync so their Angular store matches the current room state.
    // Without this, their frame/layer/cel store stays frozen at the moment they left,
    // and all previews and the main canvas show stale content.
    if (role === 'admin' && !this._joinedViaRequestJoin) return false;
    return role !== 'pending' && role !== 'none';
  }

  private _handlePixelFormatMismatch(usernames: string[]): void {
    if (this._pixelFormatMismatchHandled) return;
    this._pixelFormatMismatchHandled = true;
    const uniqueUsernames = [...new Set(usernames.filter(Boolean))];

    if (this.role$.value === 'admin') {
      // Strict mode: admins kick incompatible clients to protect room state.
      for (const username of uniqueUsernames) {
        this.awareness?.setLocalStateField('kick', { username, ts: Date.now() });
      }
      this.performanceWarning$.next('⚠ Blocked collaborator with incompatible version. Ask them to refresh and rejoin.');
      setTimeout(() => {
        this.awareness?.setLocalStateField('kick', null);
        this._pixelFormatMismatchHandled = false;
      }, 500);
      return;
    }

    // Strict mode for non-admin peers: leave immediately to avoid corrupt sync.
    this.performanceWarning$.next('⚠ Incompatible room version detected. Refresh and rejoin.');
    setTimeout(() => {
      if (this.isInRoom$.value) this.leaveRoom();
      this._pixelFormatMismatchHandled = false;
    }, 0);
  }

  /**
   * Admin-side: re-write frame data into the Yjs doc in small CHUNKS so newly
   * connected peers receive a fresh sync even if y-webrtc's initial document
   * sync failed for large documents (>256 KB can exceed the WebRTC data
   * channel's SCTP message-size limit).
   *
   * Each chunk contains at most CHUNK_SIZE frames (≈30 KB each), and chunks
   * are spaced 200 ms apart so the data channel isn't overwhelmed.
   * We do NOT use LOCAL_ORIGIN - peers must see these writes.
   */
  private _adminBroadcastFramesChunked(): void {
    if (!this.yFrames || !this.ydoc || this.role$.value !== 'admin') return;
    // Cancel any in-flight chunked broadcast
    clearTimeout(this._chunkedBroadcastTimer);

    const CHUNK_SIZE = 5; // frames per transaction - keeps each message ≈150 KB (safely under 256 KB SCTP limit)
    const CHUNK_DELAY = 80; // ms between chunks

    // Collect frame IDs to broadcast
    const frameIds: string[] = [];
    this.yFrames.forEach((_: any, fid: string) => frameIds.push(fid));


    // First, re-push frame order in its own small transaction
    if (this.yFrameOrder && this.yFrameOrder.length > 0) {
      this.ydoc.transact(() => {
        const order = this.yFrameOrder!.toArray();
        this.yFrameOrder!.delete(0, this.yFrameOrder!.length);
        this.yFrameOrder!.insert(0, order);
      }); // no LOCAL_ORIGIN - peers must see this
    }

    // Then broadcast frames in chunks with delays
    let chunkIndex = 0;
    const sendNextChunk = () => {
      if (!this.yFrames || !this.ydoc) return; // torn down
      const start = chunkIndex * CHUNK_SIZE;
      if (start >= frameIds.length) {
        // All frame chunks done – re-push cel data then layer structure so new
        // joiners can recomposite and see correct layer/frame previews.
        this._resendAllCels();
        this._resendLayers();
        return;
      }
      const chunkIds = frameIds.slice(start, start + CHUNK_SIZE);
      this.ydoc.transact(() => {
        for (const fid of chunkIds) {
          const fm = this.yFrames!.get(fid) as Y.Map<any> | undefined;
          if (!fm) continue;
          const pd = fm.get('pd') as string | undefined;
          if (pd === undefined) continue; // no pixel data for this frame yet
          // Re-set the 'pd' string so peers whose observeDeep hasn't fired yet
          // receive this as a fresh Yjs update (compact RLE, well under 256 KB).
          fm.set('pd', pd);
        }
      }); // no LOCAL_ORIGIN - peers must see this
      chunkIndex++;
      this._chunkedBroadcastTimer = setTimeout(sendNextChunk, CHUNK_DELAY);
    };

    // Start after a short delay to let the initial Yjs sync attempt finish first
    this._chunkedBroadcastTimer = setTimeout(sendNextChunk, 300);
  }

  /** Re-push all yCels entries without LOCAL_ORIGIN so new joiners receive them. */
  private _resendAllCels(): void {
    if (!this.yCels || !this.ydoc) return;
    const CEL_CHUNK = 20;
    const keys: string[] = [];
    this.yCels.forEach((_: any, key: string) => keys.push(key));
    if (keys.length === 0) return;

    let celChunkIdx = 0;
    const sendCelChunk = () => {
      if (!this.yCels || !this.ydoc) return;
      const slice = keys.slice(celChunkIdx * CEL_CHUNK, (celChunkIdx + 1) * CEL_CHUNK);
      if (slice.length === 0) return;
      this.ydoc.transact(() => {
        for (const key of slice) {
          const cm = this.yCels!.get(key) as Y.Map<any> | undefined;
          if (!cm) continue;
          const pd = cm.get('pd') as string | undefined;
          if (pd !== undefined) cm.set('pd', pd); // re-push so peers' observeDeep fires
        }
      }); // no LOCAL_ORIGIN - peers must receive this
      celChunkIdx++;
      if (celChunkIdx * CEL_CHUNK < keys.length) {
        setTimeout(sendCelChunk, 80);
      }
    };
    setTimeout(sendCelChunk, 80);
  }

  /**
   * Re-push the yLayers map entries without LOCAL_ORIGIN so new joiners
   * receive the current layer list and can render frame/layer previews correctly.
   */
  private _resendLayers(): void {
    if (!this.yLayers || !this.ydoc) return;
    const list = this.yLayers.get('list') as string | undefined;
    const activeLayerId = this.yLayers.get('activeLayerId') as string | undefined;
    if (!list) return;
    // Re-write without LOCAL_ORIGIN so remote yLayers.observe handlers fire on peers.
    this.ydoc.transact(() => {
      this.yLayers!.set('list', list);
      if (activeLayerId !== undefined) this.yLayers!.set('activeLayerId', activeLayerId);
    }); // no LOCAL_ORIGIN
  }

  private _emitFullSync(attempt = 0): void {
    if (!this._canReceiveFullRoomSync()) return;
    // Guard: once we've successfully emitted a full sync for this session, stop.
    // Multiple synced/peers events can fire in quick succession on join - without
    // this guard a second emission would overwrite the local edits the member may
    // have made after the first sync landed.
    if (this._fullSyncEmitted) return;
    if (!this.yFrames || !this.yFrameOrder) return;
    const order  = this.yFrameOrder.toArray();
    const frames: { frameId: string; pixels: string[][] }[] = [];
    let roomWidth = 0;
    let roomHeight = 0;
    this.yFrames.forEach((fm: Y.Map<any>, frameId: string) => {
      const pd  = fm.get('pd') as string | undefined;
      const w   = fm.get('w') as number;
      const h   = fm.get('h') as number;
      if (pd === undefined || !w || !h) return;
      if (roomWidth === 0) { roomWidth = w; roomHeight = h; }
      const pixels = this._decodePixels(pd, w, h);
      frames.push({ frameId, pixels });
    });
    const availableIds = new Set(frames.map(f => f.frameId));
    const validOrderedIds = order.filter(id => availableIds.has(id));
    const payloadOrder = frames.map(f => f.frameId);
    const hasOrder = order.length > 0;
    const expectedCount = hasOrder ? order.length : payloadOrder.length;
    // Stale order fallback is only allowed once we have enough frames to be confident
    // this isn't just an early partial sync. This prevents "1 blank frame" hydration.
    const staleOrderLikely =
      hasOrder &&
      attempt >= 8 &&
      validOrderedIds.length === frames.length &&
      frames.length >= Math.min(5, order.length);
    const effectiveOrder =
      staleOrderLikely ? payloadOrder
      : validOrderedIds.length > 0 ? validOrderedIds
      : payloadOrder;

    // Wait until ALL expected frames are present before emitting.  If we emit
    // with partial data (e.g. 5 of 30 frames during a slow WebRTC sync) the store
    // gets setFrames([5 frames]) and _fullSyncEmitted = true blocks further syncs.
    // On the final retry (attempt 11) we emit whatever we have as a last resort.
    const allFramesPresent = expectedCount === 0 || frames.length >= expectedCount;
    // Retry budget: scales with project size so large projects (90+ frames) get
    // enough time for the WebRTC chunked transfer to complete.
    // Formula: at least 30 attempts, or enough attempts to wait ~60 seconds,
    // whichever is larger.  Delay schedule: 300 ms × 5, 600 ms × 5, 1 s × 10,
    // 2 s × 10, then 3 s until the budget is exhausted.
    const maxAttempts = Math.max(30, Math.ceil(Math.max(order.length, expectedCount) / 5) + 20);
    if (frames.length > 0 && (allFramesPresent || staleOrderLikely || attempt >= maxAttempts)) {
      this._fullSyncEmitted = true;
      // Check for resolution mismatch
      const settings = this.collabSettings$.value;
      if (roomWidth > 0 && roomHeight > 0 && settings.canvasWidth > 0 && settings.canvasHeight > 0 &&
          (settings.canvasWidth !== roomWidth || settings.canvasHeight !== roomHeight)) {
        this.zone.run(() => this.resolutionMismatch$.next({
          roomWidth, roomHeight, localWidth: settings.canvasWidth, localHeight: settings.canvasHeight
        }));
      }
      // Snapshot cels and layers BEFORE entering zone.run so the emission is atomic.
      const celSnapshot: Record<string, string[][]> = {};
      this.yCels?.forEach((cm: Y.Map<any>, key: string) => {
        const pd = cm.get('pd') as string | undefined;
        const w  = cm.get('w') as number;
        const h  = cm.get('h') as number;
        if (pd !== undefined && w && h) celSnapshot[key] = this._decodePixels(pd, w, h);
      });
      const rawLayers       = this.yLayers?.get('list') as string | undefined;
      const activeLayerId   = (this.yLayers?.get('activeLayerId') as string | undefined) || null;
      // Emit frames + cels + layers in a single zone.run so the store receives
      // all three actions in the same Angular change-detection cycle.  This
      // prevents the brief window where syncRemoteLayers fires with an empty
      // cels map and blanks every frame's composited pixels.
      this.zone.run(() => {
        this.remoteFullSync$.next({ frames, frameOrder: effectiveOrder });
        if (Object.keys(celSnapshot).length > 0) {
          this.remoteCelChange$.next({ cels: celSnapshot });
        }
        if (rawLayers) {
          try {
            const layers = JSON.parse(rawLayers);
            this.remoteLayerSync$.next({ layers, activeLayerId });
          } catch {}
        }
      });
      this._armHydrationFallback();
    } else if (attempt < maxAttempts) {
      // Delay schedule tuned to match the faster chunked broadcast (5 frames/80 ms):
      // - First 10 attempts: 100 ms each  → covers up to ~1 s of chunk delivery
      // - Next 10 attempts:  300 ms each  → covers moderate-size projects
      // - Next 10 attempts:  600 ms each  → large projects / slow connections
      // - Beyond 30:         1 s each     → last-resort safety net
      const delay = attempt < 10 ? 100 : attempt < 20 ? 300 : attempt < 30 ? 600 : 1000;
      setTimeout(() => this._emitFullSync(attempt + 1), delay);
    } else {
      console.warn(`[Collab] _emitFullSync: GAVE UP after ${attempt} attempts (frames=${frames.length}/${expectedCount})`);
    }
  }

  /**
   * Safety-net check: directly inspect yAllowedUsers and yJoinRequests to see if
   * this pending user has been approved, regardless of whether the reactive observer
   * fired in time.  Called on `synced` and `peers` events.
   */
  private _recheckApproval(): void {
    if (this.role$.value !== 'pending' || this.isInRoom$.value || !this.localUser) return;

    // Path 1 - check yAllowedUsers directly (primary approval map)
    let myRoleRaw = this.yAllowedUsers?.get(this.localUser.username) as RoomRole | undefined;
    if (this._shouldForcePendingOnRejoin(myRoleRaw)) {
      // Auto-rejoin disabled: ignore stale allowedUsers role for non-admin refreshes.
      return;
    }
    if (myRoleRaw && myRoleRaw !== 'pending' && myRoleRaw !== 'none') {
      // Non-creator users (promoted admins who have rejoined via requestJoin) must not
      // auto-upgrade to admin from a stale yAllowedUsers entry - cap them to member.
      // The original room creator is exempt and can always recover admin role.
      let myRole = myRoleRaw;
      if (!this._isCreatorRejoin && !this._isLocalRoomCreator() && myRole === 'admin') {
        myRole = 'member';
        // Also fix the stale entry so future observers see the correct role
        try { this.yAllowedUsers!.set(this.localUser.username, 'member'); } catch {}
      }
      if (this._stopPendingBroadcast) { this._stopPendingBroadcast(); this._stopPendingBroadcast = null; }
      if (this._stopPollApproval)     { this._stopPollApproval();     this._stopPollApproval = null; }
      this._approvedViaAwareness = true;
      this.zone.run(() => {
        this.role$.next(myRole);
        this.awareness?.setLocalStateField('role', myRole);
        this.awareness?.setLocalStateField('pendingJoin', false);
        this._wasInRoom = true;
        this._clearRejoinPolicyContext();
        this.isInRoom$.next(true);
        if (myRole === 'admin') {
          this.collabHydrated$.next(true);
          this.syncing$.next(false);
        } else {
          this.collabHydrated$.next(false);
          this.syncing$.next(true);
        }
        this.persistLastRoom(myRole);
        // Reset guard so any premature emission while still pending doesn't block the approval sync
        this._fullSyncEmitted = false;
        this._emitFullSync();
      });
      return;
    }

    // Path 2 - check yJoinRequests for the approved- backup record
    if (this.yJoinRequests) {
      const approvalKey = `approved-${this.localUser.username}`;
      const record = this.yJoinRequests.get(approvalKey) as any;
      if (record?.approved) {
        const approvalTs = (record.ts as number) ?? 0;
        if (this._pendingJoinStartedAt && approvalTs < this._pendingJoinStartedAt) return;
        if (approvalTs > this._lastApprovalTs) {
          this._lastApprovalTs = approvalTs;
          const approvedRole = record.asRole as RoomRole;
          this._approvedViaAwareness = true;
          if (this._stopPendingBroadcast) { this._stopPendingBroadcast(); this._stopPendingBroadcast = null; }
          if (this._stopPollApproval)     { this._stopPollApproval();     this._stopPollApproval = null; }
          this.zone.run(() => {
            this.role$.next(approvedRole);
            this.awareness?.setLocalStateField('role', approvedRole);
            this.awareness?.setLocalStateField('pendingJoin', false);
            this._wasInRoom = true;
            this._clearRejoinPolicyContext();
            this.isInRoom$.next(true);
            if (approvedRole === 'admin') {
              this.collabHydrated$.next(true);
              this.syncing$.next(false);
            } else {
              this.collabHydrated$.next(false);
              this.syncing$.next(true);
            }
            this.persistLastRoom(approvedRole);
            // Reset guard so any premature emission while still pending doesn't block the approval sync
            this._fullSyncEmitted = false;
            this._emitFullSync();
          });
        }
      }
    }
  }

  /**
   * Active-polling fallback for approval: reads all peer awareness states every
   * 1.5 s while the local user is still pending.  Catches approvedUser signals
   * that the reactive awareness `change` event may have missed on slow connections.
   */
  private _pollForApproval(): void {
    if (!this.awareness) return;
    let pollActive = true;
    this._stopPollApproval = () => { pollActive = false; };

    const poll = () => {
      if (!pollActive || this.role$.value !== 'pending' || this.isInRoom$.value) return;

      // Primary check: inspect yAllowedUsers and yJoinRequests directly every tick.
      // This catches approvals that arrived while pending but whose reactive observer
      // callbacks were missed (e.g. delayed by the large 30-frame broadcast, or a
      // briefly unstable WebRTC connection that recovered before the next peers/synced event).
      this._recheckApproval();
      if (!pollActive) return; // _recheckApproval detected approval and stopped the loop

      const states = this.awareness?.getStates();
      if (states) {
        const localId = this.ydoc?.clientID;
        states.forEach((state: any, clientId: number) => {
          if (!pollActive) return;
          if (clientId === localId || !state?.approvedUser) return;
          if (state.approvedUser.username !== this.localUser?.username) return;

          const approvalTs = (state.approvedUser.ts as number) ?? 0;
          if (this._pendingJoinStartedAt && approvalTs < this._pendingJoinStartedAt) return;
          if (approvalTs > this._lastApprovalTs) {
            this._lastApprovalTs = approvalTs;
            pollActive = false; // stop the loop
            const approvedRole = state.approvedUser.asRole as RoomRole;
            this._approvedViaAwareness = true;
            if (this._stopPendingBroadcast) { this._stopPendingBroadcast(); this._stopPendingBroadcast = null; }
            this.zone.run(() => {
              this.role$.next(approvedRole);
              this.awareness?.setLocalStateField('role', approvedRole);
              this.awareness?.setLocalStateField('pendingJoin', false);
              this._wasInRoom = true;
              this._clearRejoinPolicyContext();
              this.isInRoom$.next(true);
            if (approvedRole === 'admin') {
              this.collabHydrated$.next(true);
              this.syncing$.next(false);
            } else {
              this.collabHydrated$.next(false);
              this.syncing$.next(true);
            }
              this.persistLastRoom(approvedRole);
              // Reset guard so any premature emission while still pending doesn't block the approval sync
              this._fullSyncEmitted = false;
              this._emitFullSync();
            });
          }
        });
      }

      if (pollActive) setTimeout(poll, 1500);
    };

    setTimeout(poll, 1500);
  }

  private _teardown(): void {
    clearTimeout(this._reconnectTimer);
    // Stop the pending-join broadcast loop and approval poll before destroying the connection
    if (this._stopPendingBroadcast) { this._stopPendingBroadcast(); this._stopPendingBroadcast = null; }
    if (this._stopPollApproval)     { this._stopPollApproval();     this._stopPollApproval = null; }
    try { this.awareness?.destroy(); } catch {}
    try { this.provider?.destroy(); } catch {}
    try { this.ydoc?.destroy(); } catch {}
    this.ydoc = null; this.provider = null; this.awareness = null;
    this.yFrames = null; this.yFrameOrder = null; this.yLayers = null; this.yCels = null;
    this.yJoinRequests = null; this.yAllowedUsers = null; this.ySettings = null;
    this.yChat = null; this.yChatMutedUsers = null; this.yBannedUsers = null; this._lastChatLength = 0; this._chatSynced = false;
    this._approvedViaAwareness = false;
    this._clearRejoinPolicyContext();
    this._isCreatorRejoin = false;
    this._joinedViaRequestJoin = false;
    this._providerSynced = false;
    this._lastApprovalTs = 0;
    this._missingAllowedRoleSinceMs = 0;
    this._lastKickTs = 0;
    this._fullSyncEmitted = false;
    this._knownPeers.clear();
    this._pixelFormatMismatchHandled = false;
    this._onlineNonCreatorAdmins.clear();
    this._lastRoomClosedTs = 0;
    this._presenceInitialized = false;
    this._lastActiveRemoteUsers.clear();
    this.roomCreator$.next(null);
    this.bannedUsers$.next([]);
    this.chatMutedUsers$.next([]);
    this.collabHydrated$.next(false);
    this.syncing$.next(false);
    clearTimeout(this._chunkedBroadcastTimer);
    clearTimeout(this._hydrateFallbackTimer);
    // Cancel all per-frame trailing-edge throttle timers
    this._broadcastThrottle.forEach(entry => clearTimeout(entry.timer));
    this._broadcastThrottle.clear();
    this._lastBroadcastMs.clear();
  }

  private _armHydrationFallback(): void {
    clearTimeout(this._hydrateFallbackTimer);
    const role = this.role$.value;
    // A rejoining admin (_joinedViaRequestJoin) starts in syncing state and needs
    // the fallback just like a member.  The original room-creator admin is always
    // authoritative (hydrated from the start), so skip the fallback for them.
    const isRejoiningAdmin = role === 'admin' && this._joinedViaRequestJoin;
    if ((role === 'admin' && !isRejoiningAdmin) || role === 'pending' || role === 'none') return;
    this._hydrateFallbackTimer = setTimeout(() => {
      if (!this.isInRoom$.value) return;
      if (this.collabHydrated$.value) return;
      if (!this.syncing$.value) return;
      console.warn('[Collab] Hydration fallback: re-emitting full sync');
      this._fullSyncEmitted = false;
      this._emitFullSync();
    }, 3000);
  }

  private _shouldForcePendingOnRejoin(role: RoomRole | undefined): boolean {
    if (!this._rejoinPolicyActive) return false;
    if (role === 'admin') return false;
    // Unknown room setting during sync is treated as "approval required" to avoid bypass.
    return this._rejoinAutoRejoinEnabled !== true;
  }

  private _isLocalRoomCreator(): boolean {
    const creator = this.ySettings?.get('roomCreator') as string | undefined;
    return !!creator && !!this.localUser?.username && creator === this.localUser.username;
  }

  private _clearRejoinPolicyContext(): void {
    this._isRejoin = false;
    this._rejoinPolicyActive = false;
    this._rejoinAutoRejoinEnabled = null;
    this._pendingJoinStartedAt = 0;
  }

  ngOnDestroy(): void { this._destroy$.next(); this._destroy$.complete(); this._teardown(); }
}
